import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardMedia,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  CircularProgress,
  LinearProgress,
  Toolbar,
  AppBar,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Stack,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Slider,
} from '@mui/material';
import {
  ArrowBack,
  PermMedia,
  Movie,
  Refresh,
  CloudUpload,
  CloudDownload,
  AutoAwesome,
  Brush,
  Save,
  PostAdd,
  CalendarMonth,
  FactCheck,
  ViewList,
  Archive,
  Unarchive,
  DeleteOutline,
} from '@mui/icons-material';
import { clientsAPI, publicFileUrl } from '../services/api';
import { registerPendingSave } from '../services/pendingSaveBeforeLogout';
import { useToast } from '../contexts/ToastContext';
import { useClients } from '../contexts/ClientContext';
import PageLoadingState from '../components/PageLoadingState';
import PageErrorAlert from '../components/PageErrorAlert';
import MlEstimateBadge from '../components/MlEstimateBadge';
import {
  MEDIA_CONTENT_PILLAR_IDS,
  MEDIA_CONTENT_PILLAR_LABELS,
  MEDIA_CONTENT_PILLAR_UPLOAD_OPTIONS,
} from '../config/contentPillars';
import { formatApiError } from '../utils/postPublishErrors';
import GalleryPostIntentField from './gallery/GalleryPostIntentField';
import GalleryTargetPlatformsField from './gallery/GalleryTargetPlatformsField';
import GalleryYouTubeSettingsField from './gallery/GalleryYouTubeSettingsField';
import {
  defaultYoutubeSettings,
  youtubePayloadFromSettings,
  youtubeSettingsFromAsset,
  youtubeSettingsFromCaptionApproval,
} from './posts/youtubeConstants';

function isImageMime(ft) {
  return (ft || '').startsWith('image/');
}

function isVideoMime(ft) {
  return (ft || '').startsWith('video/');
}

/** True when MIME or file URL extension indicates a video library asset. */
function isVideoAsset(asset) {
  if (!asset) return false;
  if (isVideoMime(asset.file_type)) return true;
  const url = String(asset.file_url || asset.storage_key || '').toLowerCase();
  return /\.(mp4|mov|webm|mkv)(\?|$)/i.test(url);
}

/** True when MIME or file URL extension indicates an image library asset. */
function isImageAsset(asset) {
  if (!asset) return false;
  if (isVideoAsset(asset)) return false;
  if (isImageMime(asset.file_type)) return true;
  const url = String(asset.file_url || asset.storage_key || '').toLowerCase();
  return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url);
}

/** JPEG poster for grid — plain thumb preferred; WM preview as fallback. */
function videoGridPosterPath(asset) {
  if (!asset) return null;
  return asset.thumbnail_file_url || asset.video_preview_wm_file_url || null;
}

/** Compact toolbar buttons — avoid multi-line “blocks” when the row wraps. */
const GALLERY_BATCH_BTN_SX = {
  whiteSpace: 'nowrap',
  py: 0.5,
  px: 1.25,
  minHeight: 32,
  lineHeight: 1.2,
  '& .MuiButton-startIcon': { mr: 0.5 },
};

function isWatermarkJobActive(asset) {
  return (
    asset.watermark_job_status === 'queued' ||
    asset.watermark_job_status === 'processing'
  );
}

/** Best URL for a “watermarked” download: full asset file, else video preview frame. */
function watermarkedDownloadPath(asset) {
  if (asset.watermarked_file_url) return asset.watermarked_file_url;
  if (isVideoAsset(asset) && asset.video_preview_wm_file_url) {
    return asset.video_preview_wm_file_url;
  }
  return null;
}

function watermarkStatusLabel(asset) {
  if (asset.watermark_job_status === 'queued' || asset.watermark_job_status === 'processing') {
    return 'Processing…';
  }
  if (asset.watermark_job_status === 'failed') {
    return 'Failed';
  }
  if (isImageMime(asset.file_type)) {
    if (asset.watermarked && asset.watermarked_file_url) return 'Watermarked';
    return 'Not watermarked';
  }
  if (isVideoMime(asset.file_type)) {
    const hasFull = Boolean(asset.watermarked_file_url);
    const hasPreview = Boolean(asset.video_preview_wm_file_url);
    if (hasFull) return 'Full video watermarked';
    if (hasPreview) return 'Preview watermarked (still image)';
    return 'Not watermarked';
  }
  return '—';
}

/** Phase 14.1 — Gallery → generate → approve → promote → schedule checklist state */
function captionWorkflowChecklist(asset, activeDraft) {
  const generated =
    Boolean(activeDraft && activeDraft.status === 'ready') ||
    Boolean(asset?.caption_status && asset.caption_status !== 'none') ||
    Boolean(asset?.approved_caption?.caption);
  const approved = Boolean(asset?.approved_caption?.caption);
  const promoted = Boolean(asset?.linked_post_id);
  return [
    {
      id: 'generate',
      label: 'Generate captions (Gemini)',
      done: generated,
    },
    {
      id: 'review',
      label: 'Review — edit, regenerate, or approve a variant',
      done: approved,
    },
    {
      id: 'promote',
      label: 'Promote to a draft post',
      done: promoted,
    },
    {
      id: 'schedule',
      label: 'Schedule from Scheduling (or batch Schedule to calendar)',
      done: false, // scheduling lives outside the asset row
      hint: promoted ? 'Open Scheduling to finish' : null,
    },
  ];
}

function assetNeedsCaptionGenerate(asset) {
  if (!asset?.client_id) return false;
  if (!(isImageAsset(asset) || isVideoAsset(asset))) return false;
  if (asset.approved_caption?.caption) return false;
  if (asset.linked_post_id) return false;
  const status = (asset.caption_status || '').toLowerCase();
  if (status === 'ready' || status === 'approved') return false;
  return true;
}

function buildVariantsPayload(localVariants) {
  return localVariants.map((v) => ({
    style: v.style,
    title: v.title,
    caption: v.caption,
    hashtags: String(v.hashtags || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => t.replace(/^#/, '')),
    review_status: v.review_status || 'draft',
  }));
}

function formatMlSlotEt(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    });
  } catch {
    return String(iso);
  }
}

function formatMlOffsetMinutes(mins) {
  if (mins === 0) return 'No change from suggestion';
  const sign = mins > 0 ? '+' : '−';
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return `${sign}${parts.join(' ')} from suggestion`;
}

function isoToDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function tagsFromHashtagStrategy(strategy, variantIndex, preferPlatform) {
  if (!strategy || typeof strategy !== 'object') return [];
  const order = [
    preferPlatform,
    'facebook',
    'instagram',
    'linkedin',
  ].filter(Boolean);
  const seen = new Set();
  for (const pk of order) {
    if (seen.has(pk)) continue;
    seen.add(pk);
    const row = strategy?.[pk]?.[variantIndex];
    const tags = row?.hashtags;
    if (Array.isArray(tags) && tags.length) {
      return tags
        .map((t) => String(t || '').trim().replace(/^#/, ''))
        .filter(Boolean);
    }
  }
  return [];
}

function normalizeLocalVariantsFromApi(
  variants,
  hashtagStrategy = null,
  preferPlatform = 'facebook',
) {
  const raw = Array.isArray(variants) ? variants : [];
  const pad = [];
  for (let i = 0; i < 3; i += 1) {
    const v = raw[i] || {};
    let hashtags = Array.isArray(v.hashtags)
      ? v.hashtags.join(', ')
      : String(v.hashtags || '');
    if (!hashtags.trim()) {
      const seeded = tagsFromHashtagStrategy(
        hashtagStrategy,
        i,
        preferPlatform,
      );
      if (seeded.length) hashtags = seeded.join(', ');
    }
    pad.push({
      style: v.style || `variant_${i}`,
      title: v.title || '',
      caption: v.caption || '',
      hashtags,
      review_status: v.review_status || 'draft',
    });
  }
  return pad;
}

/** Task 5.2: tab order from AI profile ``platform_preferences`` (instagram / facebook / linkedin only). */
function orderedFormatPlatforms(prefs) {
  const d = ['instagram', 'facebook', 'linkedin'];
  if (!prefs || typeof prefs !== 'object') return d;
  const picked = [];
  const order = prefs.priority_order || prefs.order;
  if (Array.isArray(order)) {
    for (const x of order) {
      const k = String(x).toLowerCase().trim();
      if (d.includes(k) && !picked.includes(k)) picked.push(k);
    }
  }
  const primary = String(prefs.primary || '').toLowerCase().trim();
  if (d.includes(primary) && !picked.includes(primary)) {
    picked.unshift(primary);
  }
  for (const x of d) {
    if (!picked.includes(x)) picked.push(x);
  }
  return picked;
}

/** Gallery promote targets — Instagram only via Facebook → IG link (not a standalone option). */
const GALLERY_PROMOTE_PLATFORM_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'tumblr', label: 'Tumblr' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
];
const GALLERY_PROMOTE_DEFAULT = ['facebook'];

function galleryPromotePlatformLabel(value) {
  const hit = GALLERY_PROMOTE_PLATFORM_OPTIONS.find((o) => o.value === value);
  return hit ? hit.label : value;
}

/** Normalize asset/toolbar platform lists to the Gallery allowlist. */
function normalizeGalleryPromotePlatforms(raw, fallback = GALLERY_PROMOTE_DEFAULT) {
  const arr = Array.isArray(raw) ? raw : [];
  const allowed = arr
    .map((p) => String(p || '').trim().toLowerCase())
    .map((p) => (p === 'instagram' ? 'facebook' : p))
    .filter((p) => GALLERY_PROMOTE_PLATFORM_OPTIONS.some((o) => o.value === p));
  const deduped = [];
  for (const p of allowed) {
    if (!deduped.includes(p)) deduped.push(p);
  }
  return deduped.length ? deduped : [...fallback];
}

export default function Gallery() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdParam = searchParams.get('client_id') || '';
  const batchClientId = useMemo(() => {
    if (!clientIdParam) return null;
    const n = parseInt(clientIdParam, 10);
    return Number.isFinite(n) ? n : null;
  }, [clientIdParam]);

  const { showError, showSuccess } = useToast();
  const { clients, refreshClients, setLastSelectedClientId, loaded: clientsLoaded } = useClients();
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (batchClientId) setLastSelectedClientId(batchClientId);
  }, [batchClientId, setLastSelectedClientId]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveBusyId, setArchiveBusyId] = useState(null);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState(null);
  const [preview, setPreview] = useState(null);
  const [captionDrafts, setCaptionDrafts] = useState([]);
  const [captionDraftsLoading, setCaptionDraftsLoading] = useState(false);
  const [captionBusy, setCaptionBusy] = useState(false);
  /** Per-asset post intent (blog promo, vertical, CTA) — saved on media row and merged into Gemini prompt. */
  const [captionPostIntent, setCaptionPostIntent] = useState('');
  const [captionIntentSaving, setCaptionIntentSaving] = useState(false);
  const [assetTargetPlatforms, setAssetTargetPlatforms] = useState(
    GALLERY_PROMOTE_DEFAULT,
  );
  const [assetPlatformsSaving, setAssetPlatformsSaving] = useState(false);
  const [youtubeSettings, setYoutubeSettings] = useState(() =>
    defaultYoutubeSettings(),
  );
  const [youtubeSettingsSaving, setYoutubeSettingsSaving] = useState(false);
  const [localVariants, setLocalVariants] = useState(null);
  const [hashtagPlatformTab, setHashtagPlatformTab] = useState(0);
  const captionDraftFetchIdRef = useRef(0);
  const lastSavedPayloadRef = useRef(null);
  const captionAutosaveTimerRef = useRef(null);
  const previewRef = useRef(null);
  const activeDraftRef = useRef(null);
  const localVariantsRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadContentPillar, setUploadContentPillar] = useState('motivation');
  const [uploadRightsConfirmed, setUploadRightsConfirmed] = useState(false);
  const [libraryTab, setLibraryTab] = useState('all');
  const [scrapePosts, setScrapePosts] = useState([]);
  const [loadingScrape, setLoadingScrape] = useState(false);
  const [importingIds, setImportingIds] = useState(() => new Set());

  const [typeFilter, setTypeFilter] = useState('all');
  const [pillarFilter, setPillarFilter] = useState('');
  const [watermarkFilter, setWatermarkFilter] = useState('all');
  const [captionFilter, setCaptionFilter] = useState('');
  const [batchSelectedIds, setBatchSelectedIds] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [batchPollStatus, setBatchPollStatus] = useState(null);
  const [watermarkModeSaving, setWatermarkModeSaving] = useState(false);
  const [watermarkApplyBusyId, setWatermarkApplyBusyId] = useState(null);
  const [promoteBusyId, setPromoteBusyId] = useState(null);
  const [promoteTargetPlatforms, setPromoteTargetPlatforms] = useState(
    GALLERY_PROMOTE_DEFAULT,
  );
  const promoteTargetPlatform = promoteTargetPlatforms[0] || 'facebook';
  const [batchWatermarkBusy, setBatchWatermarkBusy] = useState(false);
  const [batchReviewOpen, setBatchReviewOpen] = useState(false);
  const [batchReviewLoading, setBatchReviewLoading] = useState(false);
  const [batchReviewByAssetId, setBatchReviewByAssetId] = useState({});
  const [batchReviewApproveIds, setBatchReviewApproveIds] = useState([]);
  const [batchApproveBusy, setBatchApproveBusy] = useState(false);
  const [batchScheduleBusy, setBatchScheduleBusy] = useState(false);
  const [batchStaggerMinutes, setBatchStaggerMinutes] = useState(60);
  const [captionAiProfile, setCaptionAiProfile] = useState(null);
  const [captionEditSuggestions, setCaptionEditSuggestions] = useState(null);
  const [formatPreviewTab, setFormatPreviewTab] = useState(0);
  const [formatPreviewRows, setFormatPreviewRows] = useState(null);
  const [formatPreviewLoading, setFormatPreviewLoading] = useState(false);
  const captionWorkflowRef = useRef(null);
  const [captionGenerateBusyId, setCaptionGenerateBusyId] = useState(null);
  const [variantMlScores, setVariantMlScores] = useState(null);
  const [variantMlLoading, setVariantMlLoading] = useState(false);
  const [proposedPublishLocal, setProposedPublishLocal] = useState('');
  const [draftGateRejectReason, setDraftGateRejectReason] = useState('');
  const [draftGateRejectAmendment, setDraftGateRejectAmendment] = useState('');
  const [draftGateBusy, setDraftGateBusy] = useState(false);
  const [mlSlotPreview, setMlSlotPreview] = useState(null);
  const [mlSlotLoading, setMlSlotLoading] = useState(false);
  const [mlSlotCommitting, setMlSlotCommitting] = useState(false);
  const [mlSlotManualTime, setMlSlotManualTime] = useState('');
  const [mlSlotMode, setMlSlotMode] = useState('suggested');
  const [mlSlotOffsetMinutes, setMlSlotOffsetMinutes] = useState(0);

  const mlSlotEffectiveIso = useMemo(() => {
    if (!mlSlotPreview?.suggested_time || mlSlotMode === 'manual') return null;
    const base = new Date(mlSlotPreview.suggested_time).getTime();
    if (Number.isNaN(base)) return null;
    return new Date(base + mlSlotOffsetMinutes * 60 * 1000).toISOString();
  }, [mlSlotPreview, mlSlotMode, mlSlotOffsetMinutes]);

  const variantMlPayloadKey = useMemo(
    () =>
      JSON.stringify({
        v: buildVariantsPayload(localVariants || []),
        p: promoteTargetPlatform,
      }),
    [localVariants, promoteTargetPlatform],
  );

  const formatPlatformOrder = useMemo(
    () => orderedFormatPlatforms(captionAiProfile?.platform_preferences),
    [captionAiProfile],
  );
  const formatPreviewPlatform =
    formatPlatformOrder[formatPreviewTab] || 'instagram';

  useEffect(() => {
    if (formatPreviewTab >= formatPlatformOrder.length) {
      setFormatPreviewTab(0);
    }
  }, [formatPlatformOrder.length, formatPreviewTab]);

  useEffect(() => {
    if (!batchClientId) return undefined;
    let cancelled = false;
    clientsAPI
      .getClientAiProfile(batchClientId)
      .then((p) => {
        if (cancelled) return;
        const raw = p?.platform_preferences?.primary;
        if (typeof raw === 'string') {
          const k = raw.toLowerCase().trim();
          // Map legacy Instagram primary → Facebook (IG publishes via FB link).
          const promoteKey = k === 'instagram' ? 'facebook' : k;
          if (
            GALLERY_PROMOTE_PLATFORM_OPTIONS.some((o) => o.value === promoteKey)
          ) {
            setPromoteTargetPlatforms([promoteKey]);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [batchClientId]);

  useEffect(() => {
    const cid = preview?.client_id;
    if (!cid) {
      setCaptionAiProfile(null);
      setCaptionEditSuggestions(null);
      return undefined;
    }
    let cancelled = false;
    clientsAPI
      .getClientAiProfile(cid)
      .then((p) => {
        if (!cancelled) setCaptionAiProfile(p);
      })
      .catch(() => {
        if (!cancelled) setCaptionAiProfile(null);
      });
    clientsAPI
      .getCaptionEditSuggestions(cid, 5)
      .then((s) => {
        if (!cancelled) setCaptionEditSuggestions(s);
      })
      .catch(() => {
        if (!cancelled) setCaptionEditSuggestions(null);
      });
    return () => {
      cancelled = true;
    };
  }, [preview?.client_id]);

  useEffect(() => {
    if (!preview?.client_id || !captionAiProfile) return;
    const ord = orderedFormatPlatforms(
      captionAiProfile.platform_preferences,
    );
    const prim = String(
      captionAiProfile.platform_preferences?.primary || '',
    )
      .toLowerCase()
      .trim();
    const idx = ord.indexOf(prim);
    setFormatPreviewTab(idx >= 0 ? idx : 0);
  }, [preview?.client_id, captionAiProfile]);

  useEffect(() => {
    if (!clients.length || !clientIdParam) return;
    const ok = clients.some((c) => String(c.id) === String(clientIdParam));
    if (!ok) setSearchParams({});
  }, [clients, clientIdParam, setSearchParams]);

  useEffect(() => {
    setBatchSelectedIds([]);
    setActiveBatchId(null);
    setBatchPollStatus(null);
  }, [clientIdParam, libraryTab, showArchived]);

  const mediaListParams = useMemo(() => {
    const params =
      libraryTab === 'scraped' ? { source: 'scraped' } : {};
    // W4.F1: active library by default; toggle loads archived-only list
    params.archived = showArchived;
    return params;
  }, [libraryTab, showArchived]);

  const load = useCallback(async (opts = {}) => {
    const quiet = Boolean(opts?.quiet);
    if (!clients.length) {
      setItems([]);
      setLoading(false);
      return [];
    }
    // Phase 16.R6: quiet refreshes must not flip loading (unmounts grid → scroll jump).
    if (!quiet) {
      setLoading(true);
      setLoadError(null);
    }
    try {
      if (!clientIdParam) {
        const chunks = await Promise.all(
          clients.map(async (c) => {
            const media = await clientsAPI.listClientMedia(c.id, mediaListParams);
            const rows = Array.isArray(media) ? media : [];
            return rows.map((r) => ({
              ...r,
              _clientName: c.name,
            }));
          }),
        );
        const flat = chunks.flat();
        setItems(flat);
        return flat;
      }
      const id = parseInt(clientIdParam, 10);
      if (!Number.isFinite(id)) {
        setItems([]);
        return [];
      }
      const media = await clientsAPI.listClientMedia(id, mediaListParams);
      const arr = Array.isArray(media) ? media : [];
      setItems(arr);
      return arr;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to load media';
      if (!quiet) {
        setLoadError(msg);
        setItems([]);
      }
      return [];
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [clients, clientIdParam, mediaListParams]);

  useEffect(() => {
    load();
  }, [load]);

  // Backfill missing video posters as JPEG thumbs (never mount <video> in the grid).
  const videoThumbEnsureAttempted = useRef(new Set());
  useEffect(() => {
    videoThumbEnsureAttempted.current = new Set();
  }, [clientIdParam, libraryTab]);

  useEffect(() => {
    if (loading || !items.length) return undefined;
    const missingByClient = new Map();
    items.forEach((a) => {
      if (!isVideoAsset(a) || videoGridPosterPath(a)) return;
      if (videoThumbEnsureAttempted.current.has(a.id)) return;
      const cid = a.client_id;
      if (!cid) return;
      if (!missingByClient.has(cid)) missingByClient.set(cid, []);
      missingByClient.get(cid).push(a.id);
    });
    if (!missingByClient.size) return undefined;

    missingByClient.forEach((ids) => {
      ids.forEach((id) => videoThumbEnsureAttempted.current.add(id));
    });

    let cancelled = false;
    (async () => {
      const urlById = new Map();
      await Promise.all(
        [...missingByClient.entries()].map(async ([cid, ids]) => {
          const chunk = ids.slice(0, 40);
          try {
            const res = await clientsAPI.ensureVideoThumbnails(cid, chunk);
            (res?.results || []).forEach((r) => {
              if (r?.ok && r.thumbnail_file_url) {
                urlById.set(r.media_asset_id, r.thumbnail_file_url);
              }
            });
          } catch {
            /* keep clapperboard placeholders */
          }
        }),
      );
      if (cancelled || !urlById.size) return;
      setItems((prev) =>
        prev.map((a) => {
          const url = urlById.get(a.id);
          return url ? { ...a, thumbnail_file_url: url } : a;
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, items, clientIdParam, libraryTab]);

  const watermarkPollActive = useMemo(
    () =>
      items.some(
        (a) =>
          a.watermark_job_status === 'queued' ||
          a.watermark_job_status === 'processing',
      ),
    [items],
  );

  useEffect(() => {
    if (!watermarkPollActive) return undefined;
    const iv = setInterval(() => {
      load({ quiet: true });
    }, 2500);
    return () => clearInterval(iv);
  }, [watermarkPollActive, load]);

  /** Keep detail dialog in sync when list refreshes (e.g. watermark job finished). */
  useEffect(() => {
    setPreview((p) => {
      if (!p?.id) return p;
      const row = items.find((a) => a.id === p.id);
      if (!row) return p;
      const next = { ...p, ...row, _clientName: p._clientName };
      // Avoid identity churn → caption-draft remount → dialog scroll reset (16.R6).
      const sig = (x) =>
        JSON.stringify({
          approved_caption: x.approved_caption,
          draft_gate_status: x.draft_gate_status,
          draft_gate: x.draft_gate,
          linked_post_id: x.linked_post_id,
          watermark_job_status: x.watermark_job_status,
          watermarked_path: x.watermarked_path,
          thumbnail_path: x.thumbnail_path,
          caption_post_intent: x.caption_post_intent,
          target_platforms: x.target_platforms,
          platform_specific: x.platform_specific,
          proposed_publish_at: x.proposed_publish_at,
        });
      if (sig(p) === sig(next)) return p;
      return next;
    });
  }, [items]);

  useEffect(() => {
    if (!preview?.id) {
      setCaptionPostIntent('');
      setAssetTargetPlatforms(GALLERY_PROMOTE_DEFAULT);
      setYoutubeSettings(defaultYoutubeSettings());
      return;
    }
    setCaptionPostIntent(
      preview.caption_post_intent != null ? String(preview.caption_post_intent) : '',
    );
    // Prefer asset-stored platforms (16.R9); else seed from toolbar defaults.
    if (
      Array.isArray(preview.target_platforms) &&
      preview.target_platforms.length
    ) {
      setAssetTargetPlatforms(
        normalizeGalleryPromotePlatforms(preview.target_platforms),
      );
    } else {
      setAssetTargetPlatforms(
        normalizeGalleryPromotePlatforms(promoteTargetPlatforms),
      );
    }
    setYoutubeSettings(youtubeSettingsFromAsset(preview.platform_specific));
    // Intentionally omit promoteTargetPlatforms: toolbar changes must not wipe dialog edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed toolbar only on asset open/refresh
  }, [
    preview?.id,
    preview?.caption_post_intent,
    preview?.target_platforms,
    preview?.platform_specific,
  ]);

  const loadScrapePosts = useCallback(async () => {
    if (libraryTab !== 'scraped' || !clients.length) {
      setScrapePosts([]);
      return;
    }
    setLoadingScrape(true);
    try {
      if (!clientIdParam) {
        const chunks = await Promise.all(
          clients.map(async (c) => {
            const rows = await clientsAPI.listPublicScrapePosts(c.id);
            const arr = Array.isArray(rows) ? rows : [];
            return arr.map((r) => ({ ...r, _clientName: c.name }));
          }),
        );
        setScrapePosts(chunks.flat());
      } else {
        const id = parseInt(clientIdParam, 10);
        if (!Number.isFinite(id)) {
          setScrapePosts([]);
          return;
        }
        const rows = await clientsAPI.listPublicScrapePosts(id);
        setScrapePosts(Array.isArray(rows) ? rows : []);
      }
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Failed to load scrape posts',
      );
      setScrapePosts([]);
    } finally {
      setLoadingScrape(false);
    }
  }, [libraryTab, clients, clientIdParam, showError]);

  useEffect(() => {
    loadScrapePosts();
  }, [loadScrapePosts]);

  const uploadTargetId = useMemo(() => {
    if (!clientIdParam) return null;
    const id = parseInt(clientIdParam, 10);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [clientIdParam]);

  const pillarOptions = useMemo(() => {
    const s = new Set(MEDIA_CONTENT_PILLAR_IDS);
    items.forEach((a) => {
      if (a.content_pillar) s.add(a.content_pillar);
    });
    return [...s].sort();
  }, [items]);

  const captionOptions = useMemo(() => {
    const s = new Set();
    items.forEach((a) => {
      if (a.caption_status) s.add(a.caption_status);
    });
    return [...s].sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (typeFilter === 'image' && !isImageMime(a.file_type)) return false;
      if (typeFilter === 'video' && !isVideoMime(a.file_type)) return false;
      if (pillarFilter && a.content_pillar !== pillarFilter) return false;
      if (watermarkFilter === 'yes' && !a.watermarked) return false;
      if (watermarkFilter === 'no' && a.watermarked) return false;
      if (captionFilter && a.caption_status !== captionFilter) return false;
      return true;
    });
  }, [items, typeFilter, pillarFilter, watermarkFilter, captionFilter]);

  const scheduleEligibleIds = useMemo(() => {
    return batchSelectedIds.filter((id) => {
      const a = items.find((x) => x.id === id);
      return Boolean(a?.approved_caption?.caption && !a?.linked_post_id);
    });
  }, [batchSelectedIds, items]);

  const activeDraft = useMemo(() => {
    const rows = captionDrafts || [];
    const ready = rows.find((d) => d.status === 'ready');
    if (ready) return ready;
    return rows[0] || null;
  }, [captionDrafts]);

  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);
  useEffect(() => {
    activeDraftRef.current = activeDraft;
  }, [activeDraft]);
  useEffect(() => {
    localVariantsRef.current = localVariants;
  }, [localVariants]);

  const loadCaptionDraftsForPreview = useCallback(async (opts = {}) => {
    const quiet = Boolean(opts?.quiet);
    const p = previewRef.current;
    if (
      !p?.client_id ||
      !p?.id ||
      (!isImageAsset(p) && !isVideoAsset(p))
    ) {
      setCaptionDrafts([]);
      setLocalVariants(null);
      return;
    }
    const id = (captionDraftFetchIdRef.current += 1);
    // Quiet refetch keeps caption/calendar UI mounted (16.R6).
    if (!quiet) setCaptionDraftsLoading(true);
    try {
      const rows = await clientsAPI.listCaptionDrafts(p.client_id, p.id);
      if (id !== captionDraftFetchIdRef.current) return;
      setCaptionDrafts(Array.isArray(rows) ? rows : []);
    } catch {
      if (id !== captionDraftFetchIdRef.current) return;
      setCaptionDrafts([]);
    } finally {
      if (id === captionDraftFetchIdRef.current && !quiet) {
        setCaptionDraftsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!preview?.id) {
      setCaptionDrafts([]);
      setLocalVariants(null);
      setCaptionDraftsLoading(false);
      return;
    }
    loadCaptionDraftsForPreview();
  }, [preview?.id, preview?.client_id, loadCaptionDraftsForPreview]);

  // W4.BUG-4: sync proposed publish time only when the asset changes — not when
  // parent re-renders / asset list refresh restates the same server field.
  useEffect(() => {
    if (preview?.proposed_publish_at) {
      setProposedPublishLocal(isoToDatetimeLocalValue(preview.proposed_publish_at));
    } else {
      setProposedPublishLocal('');
    }
    setDraftGateRejectReason('');
    setDraftGateRejectAmendment('');
  }, [preview?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: hold local edits

  useEffect(() => {
    if (!activeDraft || activeDraft.status !== 'ready') {
      setLocalVariants(null);
      lastSavedPayloadRef.current = null;
      return;
    }
    const normalized = normalizeLocalVariantsFromApi(
      activeDraft.variants,
      activeDraft.hashtag_strategy,
      promoteTargetPlatform,
    );
    setLocalVariants(normalized);
    lastSavedPayloadRef.current = JSON.stringify(
      buildVariantsPayload(normalized),
    );
    // Intentionally omit promoteTargetPlatform — changing promote target must not wipe edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDraft?.id, activeDraft?.status]);

  // Fill empty hashtag fields when strategy arrives (or refreshes) after captions load.
  useEffect(() => {
    const strat = activeDraft?.hashtag_strategy;
    if (!strat || activeDraft?.status !== 'ready') return;
    setLocalVariants((prev) => {
      if (!prev?.length) return prev;
      let changed = false;
      const next = prev.map((v, i) => {
        if (String(v.hashtags || '').trim()) return v;
        const seeded = tagsFromHashtagStrategy(
          strat,
          i,
          promoteTargetPlatform,
        );
        if (!seeded.length) return v;
        changed = true;
        return { ...v, hashtags: seeded.join(', ') };
      });
      return changed ? next : prev;
    });
  }, [
    activeDraft?.id,
    activeDraft?.status,
    activeDraft?.hashtag_strategy,
    promoteTargetPlatform,
  ]);

  useEffect(() => {
    if (
      !preview?.client_id ||
      !preview?.id ||
      !activeDraft ||
      activeDraft.status !== 'ready' ||
      !localVariants?.length
    ) {
      setVariantMlScores(null);
      setVariantMlLoading(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      // Do not flip loading=true on every keystroke — keeps prior scores stable.
      try {
        const payload = {
          platform: promoteTargetPlatform,
          variants: buildVariantsPayload(localVariants),
        };
        const r = await clientsAPI.scoreCaptionVariantsMl(
          preview.client_id,
          preview.id,
          payload,
        );
        if (!cancelled) setVariantMlScores(r);
      } catch (e) {
        if (!cancelled) {
          setVariantMlScores({
            scores: [],
            model_available: false,
            _error:
              e?.response?.data?.detail || e.message || 'ML scoring failed',
          });
        }
      } finally {
        if (!cancelled) setVariantMlLoading(false);
      }
    }, 900);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    preview?.client_id,
    preview?.id,
    activeDraft?.id,
    activeDraft?.status,
    variantMlPayloadKey,
  ]);

  useEffect(() => {
    const cid = preview?.client_id;
    if (
      !cid ||
      !activeDraft ||
      activeDraft.status !== 'ready' ||
      !localVariants?.length
    ) {
      setFormatPreviewRows(null);
      return undefined;
    }
    let cancelled = false;
    const platform = formatPreviewPlatform;
    const items = localVariants.map((v, idx) => {
      let tags = String(v.hashtags || '')
        .split(',')
        .map((t) => t.trim().replace(/^#/, ''))
        .filter(Boolean);
      if (!tags.length) {
        const strat = activeDraft.hashtag_strategy;
        if (strat?.[platform]?.[idx]?.hashtags?.length) {
          tags = strat[platform][idx].hashtags;
        } else if (
          platform === 'facebook' &&
          strat?.instagram?.[idx]?.hashtags?.length
        ) {
          tags = strat.instagram[idx].hashtags;
        } else if (strat?.instagram?.[idx]?.hashtags?.length) {
          tags = strat.instagram[idx].hashtags;
        } else if (strat?.facebook?.[idx]?.hashtags?.length) {
          tags = strat.facebook[idx].hashtags;
        }
      }
      return { caption: v.caption || '', hashtags: tags };
    });
    const tmr = setTimeout(() => {
      clientsAPI
        .captionFormatBatch(cid, { platform, items })
        .then((r) => {
          if (!cancelled) {
            setFormatPreviewRows(Array.isArray(r.results) ? r.results : []);
          }
        })
        .catch(() => {
          if (!cancelled) setFormatPreviewRows(null);
        })
        .finally(() => {
          if (!cancelled) setFormatPreviewLoading(false);
        });
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(tmr);
    };
  }, [
    preview?.client_id,
    activeDraft?.id,
    activeDraft?.status,
    activeDraft?.hashtag_strategy,
    localVariants,
    formatPreviewPlatform,
  ]);

  const syncPreviewAfterLoad = useCallback(
    async (assetId) => {
      const rows = await load({ quiet: true });
      if (!rows || !assetId) return;
      const row = rows.find((a) => a.id === assetId);
      if (row) {
        setPreview((p) => (p && p.id === row.id ? { ...p, ...row } : p));
      }
    },
    [load],
  );

  const mergeDraftIntoList = useCallback((updated) => {
    if (!updated) return;
    setCaptionDrafts((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const idx = arr.findIndex((d) => d.id === updated.id);
      if (idx === -1) return [updated, ...arr];
      const next = [...arr];
      next[idx] = updated;
      return next;
    });
    if (updated.status === 'ready' && Array.isArray(updated.variants)) {
      const norm = normalizeLocalVariantsFromApi(
        updated.variants,
        updated.hashtag_strategy,
        promoteTargetPlatform,
      );
      setLocalVariants(norm);
      lastSavedPayloadRef.current = JSON.stringify(buildVariantsPayload(norm));
    } else {
      setLocalVariants(null);
      lastSavedPayloadRef.current = null;
    }
  }, [promoteTargetPlatform]);

  const flushCaptionAutosave = useCallback(async () => {
    if (captionAutosaveTimerRef.current) {
      clearTimeout(captionAutosaveTimerRef.current);
      captionAutosaveTimerRef.current = null;
    }
    const p = previewRef.current;
    const d = activeDraftRef.current;
    const vars = localVariantsRef.current;
    if (
      !p?.client_id ||
      !p?.id ||
      !d?.id ||
      d.status !== 'ready' ||
      !vars?.length
    ) {
      return;
    }
    const payload = buildVariantsPayload(vars);
    const serialized = JSON.stringify(payload);
    if (serialized === lastSavedPayloadRef.current) return;

    try {
      const updated = await clientsAPI.patchCaptionDraft(
        p.client_id,
        p.id,
        d.id,
        { variants: payload },
      );
      const nowSer = JSON.stringify(
        buildVariantsPayload(localVariantsRef.current || []),
      );
      if (nowSer === serialized) {
        mergeDraftIntoList(updated);
      } else {
        setCaptionDrafts((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          const idx = arr.findIndex((row) => row.id === updated.id);
          if (idx === -1) return [updated, ...arr];
          const next = [...arr];
          next[idx] = updated;
          return next;
        });
        lastSavedPayloadRef.current = JSON.stringify(
          buildVariantsPayload(
            normalizeLocalVariantsFromApi(updated.variants || []),
          ),
        );
      }
    } catch {
      /* 401 handled by api interceptor; keep dirty state for retry */
    }
  }, [mergeDraftIntoList]);

  useEffect(() => {
    return registerPendingSave(() => flushCaptionAutosave());
  }, [flushCaptionAutosave]);

  useEffect(() => {
    if (
      !preview?.client_id ||
      !preview?.id ||
      !activeDraft?.id ||
      activeDraft.status !== 'ready' ||
      !localVariants?.length ||
      captionBusy
    ) {
      return undefined;
    }
    const serialized = JSON.stringify(buildVariantsPayload(localVariants));
    if (serialized === lastSavedPayloadRef.current) return undefined;

    if (captionAutosaveTimerRef.current) {
      clearTimeout(captionAutosaveTimerRef.current);
    }
    captionAutosaveTimerRef.current = setTimeout(() => {
      captionAutosaveTimerRef.current = null;
      flushCaptionAutosave();
    }, 1200);
    return () => {
      if (captionAutosaveTimerRef.current) {
        clearTimeout(captionAutosaveTimerRef.current);
        captionAutosaveTimerRef.current = null;
      }
    };
  }, [
    localVariants,
    preview?.client_id,
    preview?.id,
    activeDraft?.id,
    activeDraft?.status,
    captionBusy,
    flushCaptionAutosave,
  ]);

  const updateVariantField = (idx, field, value) => {
    setLocalVariants((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleSaveCaptionDraft = async () => {
    if (!preview?.client_id || !activeDraft?.id || !localVariants) return;
    setCaptionBusy(true);
    try {
      const updated = await clientsAPI.patchCaptionDraft(
        preview.client_id,
        preview.id,
        activeDraft.id,
        { variants: buildVariantsPayload(localVariants) },
      );
      mergeDraftIntoList(updated);
      showSuccess('Caption draft saved');
      await syncPreviewAfterLoad(preview.id);
    } catch (e) {
      showError(e?.response?.data?.detail || e.message || 'Failed to save');
    } finally {
      setCaptionBusy(false);
    }
  };

  const handleApproveVariant = async (idx) => {
    if (!preview?.client_id || !activeDraft?.id || !localVariants) return;
    const approvedVariant = localVariants[idx];
    const next = localVariants.map((v, i) => {
      if (v.review_status === 'used') return v;
      if (i === idx) return { ...v, review_status: 'approved' };
      return { ...v, review_status: 'draft' };
    });
    setCaptionBusy(true);
    try {
      const updated = await clientsAPI.patchCaptionDraft(
        preview.client_id,
        preview.id,
        activeDraft.id,
        { variants: buildVariantsPayload(next) },
      );
      mergeDraftIntoList(updated);

      // W4.BUG-7: when YouTube is a target, seed title/description/tags from
      // the approved caption (still editable in YouTube settings).
      const youtubeTargeted =
        (assetTargetPlatforms || []).includes('youtube') ||
        (promoteTargetPlatforms || []).includes('youtube');
      if (youtubeTargeted && approvedVariant) {
        const filled = youtubeSettingsFromCaptionApproval({
          caption: approvedVariant.caption || '',
          hashtags: approvedVariant.hashtags || '',
          existing: youtubeSettings,
        });
        setYoutubeSettings(filled);
        try {
          const ytPayload = youtubePayloadFromSettings(filled);
          const assetUpdated = await clientsAPI.patchClientMediaAsset(
            preview.client_id,
            preview.id,
            { youtube: ytPayload },
          );
          setPreview((p) =>
            p && p.id === assetUpdated.id ? { ...p, ...assetUpdated } : p,
          );
          setItems((prev) =>
            prev.map((a) =>
              a.id === assetUpdated.id ? { ...a, ...assetUpdated } : a,
            ),
          );
          showSuccess(
            'Caption approved — YouTube title, description, and tags filled (editable)',
          );
        } catch (ytErr) {
          showSuccess('Caption approved — available to attach to posts');
          showError(
            ytErr?.response?.data?.detail ||
              ytErr.message ||
              'Caption approved, but YouTube auto-fill failed to save',
          );
        }
      } else {
        showSuccess('Caption approved — available to attach to posts');
      }
      await syncPreviewAfterLoad(preview.id);
    } catch (e) {
      showError(e?.response?.data?.detail || e.message || 'Failed to approve');
    } finally {
      setCaptionBusy(false);
    }
  };

  const handleMarkVariantUsed = async (idx) => {
    if (!preview?.client_id || !activeDraft?.id || !localVariants) return;
    const cur = localVariants[idx];
    if (!cur || cur.review_status !== 'approved') return;
    const next = localVariants.map((v, i) =>
      i === idx ? { ...v, review_status: 'used' } : v,
    );
    setCaptionBusy(true);
    try {
      const updated = await clientsAPI.patchCaptionDraft(
        preview.client_id,
        preview.id,
        activeDraft.id,
        { variants: buildVariantsPayload(next) },
      );
      mergeDraftIntoList(updated);
      showSuccess('Variant marked as used');
      await syncPreviewAfterLoad(preview.id);
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Failed to update status',
      );
    } finally {
      setCaptionBusy(false);
    }
  };

  const handleSaveCaptionPostIntent = async (rawValue) => {
    if (!preview?.client_id || !preview?.id) return;
    const nextIntent =
      rawValue !== undefined ? String(rawValue) : captionPostIntent;
    setCaptionPostIntent(nextIntent);
    setCaptionIntentSaving(true);
    try {
      const updated = await clientsAPI.patchClientMediaAsset(
        preview.client_id,
        preview.id,
        { caption_post_intent: nextIntent.trim() || null },
      );
      setPreview((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
      setItems((prev) =>
        prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
      );
      showSuccess('Post intent saved');
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Failed to save post intent',
      );
    } finally {
      setCaptionIntentSaving(false);
    }
  };

  const handleSaveAssetTargetPlatforms = useCallback(
    async (platforms) => {
      if (!preview?.client_id || !preview?.id) return;
      const cleaned = normalizeGalleryPromotePlatforms(platforms);
      setAssetTargetPlatforms(cleaned);
      setPromoteTargetPlatforms(cleaned);
      setAssetPlatformsSaving(true);
      try {
        const updated = await clientsAPI.patchClientMediaAsset(
          preview.client_id,
          preview.id,
          { target_platforms: cleaned },
        );
        setPreview((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
        setItems((prev) =>
          prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
        );
        showSuccess('Target platforms saved');
      } catch (e) {
        showError(
          e?.response?.data?.detail ||
            e.message ||
            'Failed to save target platforms',
        );
      } finally {
        setAssetPlatformsSaving(false);
      }
    },
    [preview?.client_id, preview?.id, showError, showSuccess],
  );

  const handleSaveYoutubeSettings = useCallback(
    async (youtubePayload) => {
      if (!preview?.client_id || !preview?.id) return;
      const payload =
        youtubePayload || youtubePayloadFromSettings(youtubeSettings);
      setYoutubeSettings(
        youtubeSettingsFromAsset({ youtube: payload }),
      );
      setYoutubeSettingsSaving(true);
      try {
        const updated = await clientsAPI.patchClientMediaAsset(
          preview.client_id,
          preview.id,
          { youtube: payload },
        );
        setPreview((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
        setItems((prev) =>
          prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)),
        );
        showSuccess('YouTube settings saved');
      } catch (e) {
        showError(
          e?.response?.data?.detail ||
            e.message ||
            'Failed to save YouTube settings',
        );
      } finally {
        setYoutubeSettingsSaving(false);
      }
    },
    [preview?.client_id, preview?.id, youtubeSettings, showError, showSuccess],
  );

  const handleGenerateOrRegenerateCaptions = async () => {
    if (!preview?.client_id) return;
    setCaptionBusy(true);
    try {
      const intentVal = captionPostIntent.trim() || null;
      await clientsAPI.patchClientMediaAsset(preview.client_id, preview.id, {
        caption_post_intent: intentVal,
      });
      setPreview((p) =>
        p && p.id === preview.id ? { ...p, caption_post_intent: intentVal } : p,
      );
      setItems((prev) =>
        prev.map((a) =>
          a.id === preview.id ? { ...a, caption_post_intent: intentVal } : a,
        ),
      );
      const params = {};
      if (
        preview.draft_gate_status === 'rejected' &&
        (preview.draft_gate?.brief_amendment || '').trim()
      ) {
        params.brief_amendment = preview.draft_gate.brief_amendment.trim();
      }
      const draft = await clientsAPI.generateCaptionsForMediaAsset(
        preview.client_id,
        preview.id,
        params,
      );
      mergeDraftIntoList(draft);
      await loadCaptionDraftsForPreview({ quiet: true });
      await syncPreviewAfterLoad(preview.id);
      if ((draft?.status || '').toLowerCase() === 'failed') {
        showError(
          draft?.error_message || 'Caption generation failed — try again',
        );
      } else if (draft?.pre_review?.action === 'flag') {
        showSuccess(
          'Captions generated — pre-review flagged (see warnings below)',
        );
      } else {
        showSuccess('Captions generated — review, edit, or regenerate before approving');
      }
    } catch (e) {
      showError(
        e?.response?.data?.detail ||
          e.message ||
          'Caption generation failed — try again',
      );
    } finally {
      setCaptionBusy(false);
    }
  };

  /** Open asset view focused on caption workflow (Phase 14.1 primary path). */
  const handleOpenCaptionWorkflow = useCallback((asset, e) => {
    e?.stopPropagation();
    setPreview(asset);
    // Prefer nearest so we do not yank page/dialog scroll to top (16.R6).
    requestAnimationFrame(() => {
      captionWorkflowRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }, []);

  /** One-click generate from the grid card, then open review. */
  const handleGenerateCaptionsForAsset = useCallback(
    async (asset, e) => {
      e?.stopPropagation();
      if (!asset?.client_id || !asset?.id) return;
      setCaptionGenerateBusyId(asset.id);
      setPreview(asset);
      try {
        const draft = await clientsAPI.generateCaptionsForMediaAsset(
          asset.client_id,
          asset.id,
          {},
        );
        mergeDraftIntoList(draft);
        await syncPreviewAfterLoad(asset.id);
        await loadCaptionDraftsForPreview({ quiet: true });
        if ((draft?.status || '').toLowerCase() === 'failed') {
          showError(
            draft?.error_message || 'Caption generation failed — try again',
          );
        } else {
          showSuccess(
            'Captions generated — review, edit, or regenerate before approving',
          );
        }
      } catch (err) {
        showError(
          err?.response?.data?.detail ||
            err.message ||
            'Caption generation failed — try again',
        );
      } finally {
        setCaptionGenerateBusyId(null);
      }
    },
    [
      mergeDraftIntoList,
      syncPreviewAfterLoad,
      loadCaptionDraftsForPreview,
      showError,
      showSuccess,
    ],
  );

  const handleApproveDraftPackage = async () => {
    if (!preview?.client_id || !preview?.id) return;
    if (!proposedPublishLocal) {
      showError('Choose a proposed publish time for the draft package.');
      return;
    }
    const when = new Date(proposedPublishLocal);
    if (Number.isNaN(when.getTime())) {
      showError('Invalid proposed publish time.');
      return;
    }
    const iso = when.toISOString();
    setDraftGateBusy(true);
    try {
      const updated = await clientsAPI.approveDraftPackage(
        preview.client_id,
        preview.id,
        {
          proposed_publish_at: iso,
          platform: promoteTargetPlatform,
        },
      );
      setPreview((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
      await syncPreviewAfterLoad(preview.id);
      showSuccess('Draft package approved — ready to schedule (promote / batch schedule when you want)');
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Approve draft package failed',
      );
    } finally {
      setDraftGateBusy(false);
    }
  };

  const handleRejectDraftPackage = async () => {
    if (!preview?.client_id || !preview?.id) return;
    const reason = (draftGateRejectReason || '').trim();
    if (!reason) {
      showError('Enter a rejection reason.');
      return;
    }
    setDraftGateBusy(true);
    try {
      const updated = await clientsAPI.rejectDraftPackage(
        preview.client_id,
        preview.id,
        {
          reason,
          brief_amendment: (draftGateRejectAmendment || '').trim() || undefined,
        },
      );
      setPreview((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
      await syncPreviewAfterLoad(preview.id);
      showSuccess('Draft package rejected — regenerate captions to iterate');
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Reject draft package failed',
      );
    } finally {
      setDraftGateBusy(false);
    }
  };

  const handleWithdrawDraftPackage = async () => {
    if (!preview?.client_id || !preview?.id) return;
    setDraftGateBusy(true);
    try {
      const updated = await clientsAPI.withdrawDraftPackage(
        preview.client_id,
        preview.id,
      );
      setPreview((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
      await syncPreviewAfterLoad(preview.id);
      showSuccess('Draft package gate cleared');
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Withdraw failed',
      );
    } finally {
      setDraftGateBusy(false);
    }
  };

  const handleScheduleWithMlPreview = async () => {
    if (!preview?.client_id || !preview?.id) return;
    setMlSlotLoading(true);
    setMlSlotPreview(null);
    setMlSlotMode('suggested');
    setMlSlotManualTime('');
    setMlSlotOffsetMinutes(0);
    try {
      const data = await clientsAPI.scheduleWithMlPreview(
        preview.client_id,
        preview.id,
        promoteTargetPlatform,
      );
      setMlSlotPreview(data);
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Could not fetch ML slot',
      );
    } finally {
      setMlSlotLoading(false);
    }
  };

  const handleScheduleWithMlCommit = async () => {
    if (!preview?.client_id || !preview?.id) return;
    let isoTime;
    if (mlSlotMode === 'manual') {
      const when = new Date(mlSlotManualTime);
      if (Number.isNaN(when.getTime())) {
        showError('Enter a valid date/time.');
        return;
      }
      isoTime = when.toISOString();
    } else {
      if (!mlSlotEffectiveIso) {
        showError('No ML suggestion loaded — fetch the preview first.');
        return;
      }
      isoTime = mlSlotEffectiveIso;
    }
    setMlSlotCommitting(true);
    try {
      const r = await clientsAPI.scheduleWithMlCommit(
        preview.client_id,
        preview.id,
        {
          scheduled_time: isoTime,
          platform: promoteTargetPlatform,
        },
      );
      showSuccess(
        `Post #${r.post_id} scheduled for ${new Date(r.scheduled_time).toLocaleString()}`,
      );
      setMlSlotPreview(null);
      await load();
      navigate('/scheduling?tab=calendar');
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Schedule commit failed',
      );
    } finally {
      setMlSlotCommitting(false);
    }
  };

  const handleGenerateHashtagStrategy = async () => {
    if (!preview?.client_id || !activeDraft?.id) return;
    setCaptionBusy(true);
    try {
      const updated = await clientsAPI.generateCaptionDraftHashtagStrategy(
        preview.client_id,
        preview.id,
        activeDraft.id,
      );
      mergeDraftIntoList(updated);
      showSuccess('Hashtag strategy generated');
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Hashtag strategy failed',
      );
    } finally {
      setCaptionBusy(false);
    }
  };

  const handleClosePreview = () => {
    if (captionAutosaveTimerRef.current) {
      clearTimeout(captionAutosaveTimerRef.current);
      captionAutosaveTimerRef.current = null;
    }
    captionDraftFetchIdRef.current += 1;
    setPreview(null);
    setCaptionDrafts([]);
    setLocalVariants(null);
    lastSavedPayloadRef.current = null;
  };

  const handleArchiveAsset = useCallback(
    async (asset, e) => {
      e?.stopPropagation?.();
      if (!asset?.client_id || !asset?.id) return;
      setArchiveBusyId(asset.id);
      try {
        await clientsAPI.archiveClientMediaAsset(asset.client_id, asset.id);
        showSuccess('Archived — hidden from Gallery (kept for ML)');
        if (preview?.id === asset.id) handleClosePreview();
        await load({ quiet: true });
      } catch (err) {
        showError(formatApiError(err, 'Archive failed'));
      } finally {
        setArchiveBusyId(null);
      }
    },
    [load, preview?.id, showError, showSuccess],
  );

  const handleUnarchiveAsset = useCallback(
    async (asset, e) => {
      e?.stopPropagation?.();
      if (!asset?.client_id || !asset?.id) return;
      setArchiveBusyId(asset.id);
      try {
        await clientsAPI.unarchiveClientMediaAsset(asset.client_id, asset.id);
        showSuccess('Restored to Gallery');
        if (preview?.id === asset.id) handleClosePreview();
        await load({ quiet: true });
      } catch (err) {
        showError(formatApiError(err, 'Restore failed'));
      } finally {
        setArchiveBusyId(null);
      }
    },
    [load, preview?.id, showError, showSuccess],
  );

  const handleHardDeleteAsset = useCallback(
    async (asset) => {
      if (!asset?.client_id || !asset?.id) return;
      setArchiveBusyId(asset.id);
      try {
        await clientsAPI.hardDeleteClientMediaAsset(asset.client_id, asset.id);
        showSuccess('Permanently deleted');
        setHardDeleteConfirm(null);
        if (preview?.id === asset.id) handleClosePreview();
        await load({ quiet: true });
      } catch (err) {
        showError(formatApiError(err, 'Hard delete failed'));
      } finally {
        setArchiveBusyId(null);
      }
    },
    [load, preview?.id, showError, showSuccess],
  );

  const handleClientFilterChange = (e) => {
    const v = e.target.value;
    if (v === '') {
      setSearchParams({});
    } else {
      const parsed = parseInt(String(v), 10);
      if (Number.isFinite(parsed) && parsed > 0) {
        setLastSelectedClientId(parsed);
      }
      setSearchParams({ client_id: String(v) });
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploadTargetId == null) return;
    if (!uploadRightsConfirmed) {
      showError(
        'Check "I have the rights to use this asset." before uploading.',
      );
      return;
    }
    setUploading(true);
    try {
      await clientsAPI.uploadClientMedia(uploadTargetId, file, {
        source: 'upload',
        content_pillar: uploadContentPillar,
        rights_confirmed: true,
      });
      showSuccess('Upload complete');
      await load();
    } catch (err) {
      showError(formatApiError(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleImportScrape = async (row) => {
    const cid = row.client_id;
    if (!cid || row.already_in_library) return;
    setImportingIds((prev) => new Set(prev).add(row.id));
    try {
      const results = await clientsAPI.importPublicScrapeToLibrary(cid, [
        row.id,
      ]);
      const r = Array.isArray(results) ? results[0] : null;
      if (r?.ok) {
        showSuccess('Imported to library');
        await load();
        await loadScrapePosts();
      } else {
        showError(r?.detail || 'Import failed');
      }
    } catch (err) {
      showError(
        err?.response?.data?.detail || err.message || 'Import failed',
      );
    } finally {
      setImportingIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const clientLabel =
    !clientIdParam || !clients.length
      ? 'All clients'
      : clients.find((c) => String(c.id) === String(clientIdParam))?.name ||
        `Client ${clientIdParam}`;

  const selectedClient = useMemo(() => {
    if (!clientIdParam || !clients.length) return null;
    return clients.find((c) => String(c.id) === String(clientIdParam)) || null;
  }, [clients, clientIdParam]);

  const handleWatermarkUploadModeChange = async (e) => {
    const v = e.target.value;
    if (!selectedClient?.id) return;
    setWatermarkModeSaving(true);
    try {
      await clientsAPI.updateClient(selectedClient.id, {
        watermark_upload_mode: v,
      });
      await refreshClients({ force: true });
      showSuccess('Watermark on upload setting saved');
    } catch (err) {
      showError(
        err?.response?.data?.detail ||
          err.message ||
          'Failed to update watermark setting',
      );
    } finally {
      setWatermarkModeSaving(false);
    }
  };

  const openPublicDownload = useCallback((path) => {
    const url = publicFileUrl(path);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleDownloadOriginal = useCallback(
    (asset, e) => {
      e?.stopPropagation();
      openPublicDownload(asset.file_url);
    },
    [openPublicDownload],
  );

  const handleDownloadWatermarked = useCallback(
    (asset, e) => {
      e?.stopPropagation();
      const p = watermarkedDownloadPath(asset);
      if (p) openPublicDownload(p);
    },
    [openPublicDownload],
  );

  const runWatermarkApply = useCallback(
    async (asset, apiFn) => {
      if (!asset.client_id) return;
      setWatermarkApplyBusyId(asset.id);
      try {
        await apiFn(asset.client_id, asset.id);
        showSuccess('Watermark applied');
        await load();
      } catch (err) {
        showError(
          err?.response?.data?.detail ||
            err.message ||
            'Watermark failed',
        );
      } finally {
        setWatermarkApplyBusyId(null);
      }
    },
    [load, showError, showSuccess],
  );

  const handleApplyImageWatermark = useCallback(
    (asset, e) => {
      e?.stopPropagation();
      if (isWatermarkJobActive(asset)) return;
      runWatermarkApply(asset, clientsAPI.applyWatermarkMedia);
    },
    [runWatermarkApply],
  );

  const handleApplyVideoPreviewWatermark = useCallback(
    (asset, e) => {
      e?.stopPropagation();
      if (isWatermarkJobActive(asset)) return;
      runWatermarkApply(asset, clientsAPI.applyWatermarkVideoThumbnail);
    },
    [runWatermarkApply],
  );

  const handleApplyVideoFullWatermark = useCallback(
    (asset, e) => {
      e?.stopPropagation();
      if (isWatermarkJobActive(asset)) return;
      runWatermarkApply(asset, clientsAPI.applyWatermarkVideoFull);
    },
    [runWatermarkApply],
  );

  const handlePromoteToPost = useCallback(
    async (asset, e) => {
      e?.stopPropagation();
      const cid = asset.client_id;
      if (!cid || !asset.id) return;
      if (asset.linked_post_id) return;
      if (!asset.approved_caption?.caption) {
        setPreview(asset);
        showError(
          'Approve a caption variant first (Generate → edit/regenerate → Approve), then promote.',
        );
        requestAnimationFrame(() => {
          captionWorkflowRef.current?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'nearest',
          });
        });
        return;
      }
      setPromoteBusyId(asset.id);
      try {
        // Prefer dialog selection for the open asset; else stored asset platforms; else toolbar.
        let platforms;
        if (preview?.id === asset.id && assetTargetPlatforms?.length) {
          platforms = normalizeGalleryPromotePlatforms(assetTargetPlatforms);
          try {
            await clientsAPI.patchClientMediaAsset(cid, asset.id, {
              target_platforms: platforms,
            });
          } catch {
            /* promote still uses platforms below */
          }
        } else if (
          Array.isArray(asset.target_platforms) &&
          asset.target_platforms.length
        ) {
          platforms = normalizeGalleryPromotePlatforms(asset.target_platforms);
        } else {
          platforms =
            batchClientId != null && cid === batchClientId
              ? promoteTargetPlatforms
              : GALLERY_PROMOTE_DEFAULT;
        }
        const vi = asset?.approved_caption?.variant_index;
        const variantIndex =
          vi === undefined || vi === null
            ? undefined
            : Number.isFinite(Number(vi))
              ? Math.trunc(Number(vi))
              : undefined;
        const promoteOpts = {
          platforms,
          content_pillar: asset.content_pillar || undefined,
          variant_index: variantIndex,
        };
        if ((platforms || []).includes('youtube')) {
          const ytPayload =
            preview?.id === asset.id
              ? youtubePayloadFromSettings(youtubeSettings)
              : youtubePayloadFromSettings(
                  youtubeSettingsFromAsset(asset.platform_specific),
                );
          promoteOpts.youtube = ytPayload;
          try {
            await clientsAPI.patchClientMediaAsset(cid, asset.id, {
              youtube: ytPayload,
            });
          } catch {
            /* promote body still carries youtube below */
          }
        }
        const data = await clientsAPI.promoteMediaAssetToPost(
          cid,
          asset.id,
          promoteOpts,
        );
        const n = Array.isArray(data?.posts) ? data.posts.length : 1;
        showSuccess(
          n > 1
            ? `Created ${n} draft posts (#${data.id} + ${n - 1} more)`
            : `Draft post #${data.id} created`,
        );
        await load();
        navigate(`/scheduling?tab=bulk`);
      } catch (err) {
        showError(formatApiError(err, 'Could not create post'));
      } finally {
        setPromoteBusyId(null);
      }
    },
    [
      load,
      navigate,
      showError,
      showSuccess,
      batchClientId,
      promoteTargetPlatforms,
      preview?.id,
      assetTargetPlatforms,
      youtubeSettings,
    ],
  );

  const canBatchSelect = Boolean(batchClientId) && libraryTab === 'all';

  const toggleBatchSelect = (assetId) => {
    setBatchSelectedIds((prev) =>
      prev.includes(assetId)
        ? prev.filter((x) => x !== assetId)
        : [...prev, assetId],
    );
  };

  const handleStartCaptionBatch = async () => {
    if (!batchClientId || !batchSelectedIds.length) return;
    const ids = batchSelectedIds.filter((id) => {
      const a = items.find((x) => x.id === id);
      return a && (isImageAsset(a) || isVideoAsset(a));
    });
    if (!ids.length) {
      showError('Select at least one image or video asset.');
      return;
    }
    try {
      const r = await clientsAPI.startCaptionBatch(batchClientId, ids);
      setActiveBatchId(r.batch_id);
      const st = await clientsAPI.getCaptionBatch(batchClientId, r.batch_id);
      setBatchPollStatus(st);
      showSuccess('Batch queued — captions run in the background');
    } catch (e) {
      showError(formatApiError(e, 'Failed to start batch'));
    }
  };

  const refreshBatchReviewDrafts = useCallback(async () => {
    if (!batchClientId || !batchSelectedIds.length) return;
    const entries = await Promise.all(
      batchSelectedIds.map(async (id) => {
        try {
          const drafts = await clientsAPI.listCaptionDrafts(batchClientId, id);
          return [
            id,
            { drafts: Array.isArray(drafts) ? drafts : [], error: null },
          ];
        } catch (err) {
          return [
            id,
            {
              drafts: [],
              error:
                err?.response?.data?.detail ||
                err.message ||
                'Failed to load drafts',
            },
          ];
        }
      }),
    );
    setBatchReviewByAssetId(Object.fromEntries(entries));
  }, [batchClientId, batchSelectedIds]);

  const handleBatchWatermark = async () => {
    if (!batchClientId || !batchSelectedIds.length) return;
    const ids = batchSelectedIds.filter((id) => {
      const a = items.find((x) => x.id === id);
      return a && (isImageMime(a.file_type) || isVideoMime(a.file_type));
    });
    if (!ids.length) {
      showError('Select at least one image or video.');
      return;
    }
    setBatchWatermarkBusy(true);
    try {
      const r = await clientsAPI.batchWatermarkMedia(batchClientId, ids);
      const bad = (r.results || []).filter((x) => !x.ok);
      await load();
      if (bad.length) {
        showError(
          `Watermark: ${bad.length} failed${
            bad[0]?.detail ? ` — ${String(bad[0].detail).slice(0, 200)}` : ''
          }`,
        );
      } else {
        showSuccess('Watermark batch finished');
      }
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Batch watermark failed',
      );
    } finally {
      setBatchWatermarkBusy(false);
    }
  };

  const openBatchReview = async () => {
    if (!batchClientId || !batchSelectedIds.length) return;
    setBatchReviewOpen(true);
    setBatchReviewApproveIds([]);
    setBatchReviewLoading(true);
    setBatchReviewByAssetId({});
    try {
      await refreshBatchReviewDrafts();
    } finally {
      setBatchReviewLoading(false);
    }
  };

  const toggleBatchReviewApprove = (assetId) => {
    setBatchReviewApproveIds((prev) =>
      prev.includes(assetId)
        ? prev.filter((x) => x !== assetId)
        : [...prev, assetId],
    );
  };

  const handleBatchApproveChecked = async () => {
    if (!batchClientId || !batchReviewApproveIds.length) return;
    setBatchApproveBusy(true);
    try {
      const r = await clientsAPI.batchApproveCaptions(
        batchClientId,
        batchReviewApproveIds,
      );
      const bad = (r.results || []).filter((x) => !x.ok);
      await load();
      await refreshBatchReviewDrafts();
      setBatchReviewApproveIds([]);
      if (bad.length) {
        showError(
          `Approve: ${bad.length} skipped${
            bad[0]?.detail ? ` — ${bad[0].detail}` : ''
          }`,
        );
      } else {
        showSuccess('Captions approved for selected assets');
      }
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Batch approve failed',
      );
    } finally {
      setBatchApproveBusy(false);
    }
  };

  const handleBatchScheduleToCalendar = async () => {
    if (!batchClientId || !scheduleEligibleIds.length) return;
    const stagger = Math.max(
      0,
      parseInt(String(batchStaggerMinutes), 10) || 0,
    );
    setBatchScheduleBusy(true);
    try {
      const r = await clientsAPI.batchScheduleToCalendar(batchClientId, {
        media_asset_ids: scheduleEligibleIds,
        platform: promoteTargetPlatform,
        stagger_minutes: stagger,
      });
      const bad = (r.results || []).filter((x) => !x.ok);
      const ok = (r.results || []).filter((x) => x.ok);
      await load();
      if (bad.length && !ok.length) {
        showError(
          bad.map((b) => b.detail || 'failed').join('; ') ||
            'Schedule batch failed',
        );
      } else {
        showSuccess(
          `Scheduled ${ok.length} post(s)${
            bad.length ? `; ${bad.length} skipped` : ''
          }`,
        );
        if (ok.length) navigate('/scheduling?tab=calendar');
      }
    } catch (e) {
      showError(
        e?.response?.data?.detail || e.message || 'Batch schedule failed',
      );
    } finally {
      setBatchScheduleBusy(false);
    }
  };

  useEffect(() => {
    if (!activeBatchId || !batchClientId) return undefined;
    let cancelled = false;
    let iv;
    const tick = async () => {
      try {
        const st = await clientsAPI.getCaptionBatch(
          batchClientId,
          activeBatchId,
        );
        if (cancelled) return;
        setBatchPollStatus(st);
        if (st.status === 'completed' || st.status === 'failed') {
          if (iv) clearInterval(iv);
          await load();
          if (cancelled) return;
          if (st.status === 'failed') {
            showError(st.error_message || 'Batch job failed');
          } else if (st.failed > 0) {
            showSuccess(
              `Batch finished: ${st.completed} ok, ${st.failed} failed`,
            );
          } else {
            showSuccess('Batch captioning finished');
          }
          setActiveBatchId(null);
          setBatchPollStatus(null);
          setBatchSelectedIds([]);
        }
      } catch (e) {
        if (!cancelled) {
          showError(
            e?.response?.data?.detail || e.message || 'Batch status failed',
          );
        }
      }
    };
    tick();
    iv = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
    };
  }, [activeBatchId, batchClientId, load, showError, showSuccess]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0d1117' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#161b22',
          borderBottom: '1px solid #30363d',
        }}
      >
        <Toolbar sx={{ gap: 1, flexWrap: 'wrap', py: 1, px: { xs: 1, sm: 2 }, maxWidth: '100%' }}>
          <Tooltip title="Back to clients">
            <IconButton
              edge="start"
              onClick={() => navigate('/clients')}
              sx={{ color: '#f0f6fc' }}
            >
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <PermMedia sx={{ color: '#58a6ff' }} />
          <Box sx={{ flexGrow: 1, minWidth: 0, flexBasis: { xs: '100%', sm: 'auto' } }}>
            <Typography variant="h6" sx={{ color: '#f0f6fc', fontWeight: 700 }}>
              Gallery
            </Typography>
            <Typography variant="body2" sx={{ color: '#8b949e' }}>
              {clientLabel}
              {clientIdParam ? ` · #${clientIdParam}` : ''}
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
            <InputLabel id="gallery-client-filter" sx={{ color: '#8b949e' }}>
              Client
            </InputLabel>
            <Select
              labelId="gallery-client-filter"
              label="Client"
              value={clientIdParam}
              displayEmpty
              onChange={handleClientFilterChange}
              sx={{
                color: '#f0f6fc',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#30363d' },
              }}
            >
              <MenuItem value="">
                <em>All clients</em>
              </MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {libraryTab === 'all' && clientIdParam && (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel
                id="gallery-watermark-upload-mode"
                sx={{ color: '#8b949e' }}
              >
                Watermark on upload
              </InputLabel>
              <Select
                labelId="gallery-watermark-upload-mode"
                label="Watermark on upload"
                value={selectedClient?.watermark_upload_mode || 'manual'}
                onChange={handleWatermarkUploadModeChange}
                disabled={!selectedClient || watermarkModeSaving}
                sx={{
                  color: '#f0f6fc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#30363d',
                  },
                }}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="auto">Auto</MenuItem>
              </Select>
            </FormControl>
          )}

          {libraryTab === 'all' && clientIdParam && (
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
              <InputLabel
                id="gallery-promote-platform"
                sx={{ color: '#8b949e' }}
              >
                Promote draft as
              </InputLabel>
              <Select
                labelId="gallery-promote-platform"
                label="Promote draft as"
                multiple
                value={promoteTargetPlatforms}
                onChange={(e) => {
                  const next = e.target.value;
                  const arr = typeof next === 'string' ? next.split(',') : next;
                  const allowed = arr.filter((p) =>
                    GALLERY_PROMOTE_PLATFORM_OPTIONS.some((o) => o.value === p),
                  );
                  setPromoteTargetPlatforms(
                    allowed.length ? allowed : GALLERY_PROMOTE_DEFAULT,
                  );
                }}
                renderValue={(selected) =>
                  (selected || []).map(galleryPromotePlatformLabel).join(', ')
                }
                sx={{
                  color: '#f0f6fc',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#30363d',
                  },
                }}
              >
                {GALLERY_PROMOTE_PLATFORM_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox
                      size="small"
                      checked={promoteTargetPlatforms.includes(opt.value)}
                      sx={{ color: '#8b949e', py: 0 }}
                    />
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: '#8b949e', mx: 0, mt: 0.5 }}>
                Instagram posts via Facebook → Instagram link — enable on Clients
              </FormHelperText>
            </FormControl>
          )}

          {libraryTab === 'all' && (
            <>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                <InputLabel id="gallery-upload-pillar" sx={{ color: '#8b949e' }}>
                  Pillar (upload)
                </InputLabel>
                <Select
                  labelId="gallery-upload-pillar"
                  label="Pillar (upload)"
                  value={uploadContentPillar}
                  onChange={(e) => setUploadContentPillar(e.target.value)}
                  disabled={uploadTargetId == null}
                  sx={{
                    color: '#f0f6fc',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#30363d',
                    },
                  }}
                >
                  {MEDIA_CONTENT_PILLAR_UPLOAD_OPTIONS.map((id) => (
                    <MenuItem key={id} value={id}>
                      {MEDIA_CONTENT_PILLAR_LABELS[id] || id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControlLabel
                sx={{
                  m: 0,
                  maxWidth: 280,
                  alignItems: 'flex-start',
                  '& .MuiFormControlLabel-label': {
                    color: '#8b949e',
                    fontSize: 13,
                    lineHeight: 1.35,
                  },
                }}
                control={
                  <Checkbox
                    size="small"
                    checked={uploadRightsConfirmed}
                    onChange={(e) => setUploadRightsConfirmed(e.target.checked)}
                    disabled={uploadTargetId == null}
                    sx={{ color: '#8b949e', py: 0.5 }}
                  />
                }
                label="I have the rights to use this asset."
              />

              <Button
                variant="outlined"
                component="label"
                disabled={
                  uploading ||
                  loading ||
                  uploadTargetId == null ||
                  !uploadRightsConfirmed
                }
                startIcon={
                  uploading ? <CircularProgress size={18} /> : <CloudUpload />
                }
                sx={{ borderColor: '#30363d', color: '#f0f6fc' }}
                title={
                  uploadTargetId == null
                    ? 'Select a single client to enable upload'
                    : !uploadRightsConfirmed
                      ? 'Confirm usage rights to enable upload'
                      : ''
                }
              >
                Upload
                <input
                  type="file"
                  hidden
                  accept="image/*,video/*"
                  onChange={handleUpload}
                />
              </Button>
            </>
          )}
          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={load}
                disabled={loading}
                sx={{ color: '#f0f6fc' }}
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        <PageErrorAlert message={loadError} onClose={() => setLoadError(null)} />

        <Tabs
          value={libraryTab}
          onChange={(_, v) => setLibraryTab(v)}
          sx={{
            mb: 2,
            minHeight: 40,
            '& .MuiTab-root': { color: '#8b949e', textTransform: 'none' },
            '& .Mui-selected': { color: '#58a6ff !important' },
            '& .MuiTabs-indicator': { bgcolor: '#58a6ff' },
          }}
        >
          <Tab label="All library" value="all" />
          <Tab label="Scraped (Instagram)" value="scraped" />
        </Tabs>

        {libraryTab === 'all' &&
          uploadTargetId == null &&
          clients.length > 0 && (
            <Typography variant="body2" sx={{ color: '#6e7681', mb: 2 }}>
              Choose a <strong>client</strong> in the dropdown to upload new
              files into that library. <strong>All clients</strong> shows every
              client&apos;s media together.
            </Typography>
          )}

        {libraryTab === 'scraped' && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: '1px solid #30363d',
              borderRadius: 2,
              bgcolor: '#161b22',
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ color: '#f0f6fc', mb: 1, fontWeight: 600 }}
            >
              <CloudDownload
                sx={{ verticalAlign: 'middle', mr: 0.75, fontSize: 22 }}
              />
              Import from Instagram scrape
            </Typography>
            <Typography variant="body2" sx={{ color: '#8b949e', mb: 2 }}>
              Queue a public profile scrape from{' '}
              <strong>Clients → Platform accounts</strong>, then import rows
              into the library as{' '}
              <code style={{ color: '#79c0ff' }}>source: scraped</code>. If
              import says there is no media URL, apply{' '}
              <code style={{ color: '#79c0ff' }}>
                migrations/add_client_posts_scrape_media.sql
              </code>{' '}
              and re-scrape.
            </Typography>
            {loadingScrape ? (
              <Box sx={{ display: 'flex', py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            ) : scrapePosts.length === 0 ? (
              <Typography variant="body2" sx={{ color: '#6e7681' }}>
                No <code>public_scrape</code> rows for the selected scope yet.
              </Typography>
            ) : (
              <Grid container spacing={1.5}>
                {scrapePosts.map((row) => {
                  const busy = importingIds.has(row.id);
                  const key = `${row.client_id}-${row.id}`;
                  const hasMedia =
                    Boolean(row.preview_url) ||
                    (Array.isArray(row.scrape_media?.images) &&
                      row.scrape_media.images.length > 0) ||
                    (Array.isArray(row.scrape_media?.videos) &&
                      row.scrape_media.videos.length > 0);
                  return (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={key}>
                      <Card
                        sx={{
                          bgcolor: '#0d1117',
                          border: '1px solid #30363d',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            pt: '100%',
                            bgcolor: '#010409',
                          }}
                        >
                          {row.preview_url ? (
                            <Box
                              component="img"
                              src={row.preview_url}
                              alt=""
                              referrerPolicy="no-referrer"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%,-50%)',
                                color: '#6e7681',
                                px: 1,
                                textAlign: 'center',
                              }}
                            >
                              No preview
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ p: 1, flexGrow: 1 }}>
                          {!clientIdParam && row._clientName && (
                            <Chip
                              label={row._clientName}
                              size="small"
                              sx={{
                                mb: 0.5,
                                height: 20,
                                fontSize: 10,
                                maxWidth: '100%',
                              }}
                            />
                          )}
                          {row.already_in_library ? (
                            <Chip
                              label="In library"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ height: 24, fontSize: 11 }}
                            />
                          ) : (
                            <Button
                              size="small"
                              fullWidth
                              variant="contained"
                              disabled={busy || !hasMedia}
                              onClick={() => handleImportScrape(row)}
                              sx={{ mt: 0.5, fontSize: 11 }}
                            >
                              {busy ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                'Import'
                              )}
                            </Button>
                          )}
                          {!hasMedia && !row.already_in_library && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#6e7681', display: 'block', mt: 0.5 }}
                            >
                              Re-scrape after migration
                            </Typography>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        )}

        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="type-filter" sx={{ color: '#8b949e' }}>
                Type
              </InputLabel>
              <Select
                labelId="type-filter"
                label="Type"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                sx={{ color: '#f0f6fc', borderColor: '#30363d' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="pillar-filter" sx={{ color: '#8b949e' }}>
                Content pillar
              </InputLabel>
              <Select
                labelId="pillar-filter"
                label="Content pillar"
                value={pillarFilter}
                onChange={(e) => setPillarFilter(e.target.value)}
                sx={{ color: '#f0f6fc' }}
              >
                <MenuItem value="">All</MenuItem>
                {pillarOptions.map((p) => (
                  <MenuItem key={p} value={p}>
                    {MEDIA_CONTENT_PILLAR_LABELS[p] || p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="wm-filter" sx={{ color: '#8b949e' }}>
                Watermark
              </InputLabel>
              <Select
                labelId="wm-filter"
                label="Watermark"
                value={watermarkFilter}
                onChange={(e) => setWatermarkFilter(e.target.value)}
                sx={{ color: '#f0f6fc' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Watermarked</MenuItem>
                <MenuItem value="no">Not watermarked</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="cap-filter" sx={{ color: '#8b949e' }}>
                Caption status
              </InputLabel>
              <Select
                labelId="cap-filter"
                label="Caption status"
                value={captionFilter}
                onChange={(e) => setCaptionFilter(e.target.value)}
                sx={{ color: '#f0f6fc' }}
              >
                <MenuItem value="">All</MenuItem>
                {captionOptions.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Chip
              label={`${filtered.length} shown`}
              size="small"
              sx={{ bgcolor: '#21262d', color: '#8b949e' }}
            />
            <Button
              size="small"
              variant={showArchived ? 'contained' : 'outlined'}
              startIcon={showArchived ? <Unarchive /> : <Archive />}
              onClick={() => setShowArchived((v) => !v)}
              sx={{
                ...(showArchived
                  ? { bgcolor: '#6e7681', '&:hover': { bgcolor: '#8b949e' } }
                  : {
                      color: '#c9d1d9',
                      borderColor: '#30363d',
                    }),
                textTransform: 'none',
              }}
            >
              {showArchived ? 'Showing archived' : 'Show archived'}
            </Button>
          </Stack>

          {canBatchSelect && (
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
            >
              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: '#c9d1d9',
                  borderColor: '#30363d',
                  ...GALLERY_BATCH_BTN_SX,
                }}
                onClick={() =>
                  setBatchSelectedIds(
                    filtered
                      .filter(
                        (a) =>
                          isImageMime(a.file_type) || isVideoMime(a.file_type),
                      )
                      .map((a) => a.id),
                  )
                }
              >
                Select visible
              </Button>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  color: '#c9d1d9',
                  borderColor: '#30363d',
                  ...GALLERY_BATCH_BTN_SX,
                }}
                onClick={() => setBatchSelectedIds([])}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Brush sx={{ fontSize: 16 }} />}
                disabled={
                  !batchSelectedIds.length ||
                  batchWatermarkBusy ||
                  Boolean(activeBatchId)
                }
                onClick={handleBatchWatermark}
                sx={{
                  color: '#c9d1d9',
                  borderColor: '#30363d',
                  ...GALLERY_BATCH_BTN_SX,
                }}
              >
                Watermark
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
                disabled={
                  !batchSelectedIds.length || Boolean(activeBatchId)
                }
                onClick={handleStartCaptionBatch}
                sx={{
                  bgcolor: '#238636',
                  '&:hover': { bgcolor: '#2ea043' },
                  ...GALLERY_BATCH_BTN_SX,
                }}
              >
                Captions ({batchSelectedIds.length})
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ViewList sx={{ fontSize: 16 }} />}
                disabled={!batchSelectedIds.length}
                onClick={openBatchReview}
                sx={{
                  color: '#c9d1d9',
                  borderColor: '#30363d',
                  ...GALLERY_BATCH_BTN_SX,
                }}
              >
                Review
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<CalendarMonth sx={{ fontSize: 16 }} />}
                disabled={
                  !scheduleEligibleIds.length ||
                  batchScheduleBusy ||
                  Boolean(activeBatchId)
                }
                onClick={handleBatchScheduleToCalendar}
                sx={{
                  bgcolor: '#8957e5',
                  '&:hover': { bgcolor: '#a371f7' },
                  ...GALLERY_BATCH_BTN_SX,
                }}
              >
                Schedule ({scheduleEligibleIds.length})
              </Button>
            </Stack>
          )}
        </Stack>

        {canBatchSelect && (
          <Alert severity="info" icon={<AutoAwesome />} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Caption workflow
            </Typography>
            <Typography variant="body2" component="div">
              1. Upload → 2. <strong>Generate caption</strong> (card or batch) → 3. Approve /
              edit / regenerate → 4. Promote to post → 5. Schedule. Select images or videos and use{' '}
              <strong>Generate captions</strong> for a one-click batch.
            </Typography>
          </Alert>
        )}

        {activeBatchId && batchPollStatus && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: '#161b22',
              border: '1px solid #30363d',
            }}
          >
            <Typography variant="body2" sx={{ color: '#8b949e', mb: 1 }}>
              Batch captions — {batchPollStatus.status} ·{' '}
              {batchPollStatus.completed + batchPollStatus.failed} /{' '}
              {batchPollStatus.total} processed
              {batchPollStatus.failed > 0
                ? ` · ${batchPollStatus.failed} failed`
                : ''}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                batchPollStatus.total > 0
                  ? (100 *
                      (batchPollStatus.completed + batchPollStatus.failed)) /
                    batchPollStatus.total
                  : 0
              }
              sx={{
                height: 8,
                borderRadius: 1,
                bgcolor: '#21262d',
                '& .MuiLinearProgress-bar': { bgcolor: '#238636' },
              }}
            />
          </Box>
        )}

        {!clientsLoaded || loading ? (
          <PageLoadingState minHeight={400} message="Loading media library…" />
        ) : filtered.length === 0 ? (
          <Typography sx={{ color: '#8b949e' }}>
            {libraryTab === 'scraped'
              ? 'No scraped assets in the library yet. Use Import above after a public profile scrape.'
              : 'No files match these filters. Upload from a client view or adjust filters.'}
          </Typography>
        ) : (
          <Box
            aria-hidden={Boolean(preview) || undefined}
            sx={{
              display: 'grid',
              gap: 1.25,
              // Keep grid mounted (and sized) while dialog is open so page scroll holds (16.R6).
              visibility: preview ? 'hidden' : 'visible',
              pointerEvents: preview ? 'none' : 'auto',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
                lg: 'repeat(6, minmax(0, 1fr))',
                xl: 'repeat(8, minmax(0, 1fr))',
              },
            }}
          >
            {filtered.map((asset) => {
              const src = publicFileUrl(asset.file_url);
              const img = isImageAsset(asset);
              const vid = isVideoAsset(asset);
              return (
                <Card
                  key={asset.id}
                  sx={{
                    bgcolor: '#161b22',
                    border: '1px solid #30363d',
                    cursor: 'pointer',
                    contentVisibility: 'auto',
                    containIntrinsicSize: '0 220px',
                    minWidth: 0,
                    '&:hover': { borderColor: '#58a6ff' },
                  }}
                  onClick={(e) => {
                    if (e.target.closest('[data-batch-checkbox]')) return;
                    startTransition(() => setPreview(asset));
                  }}
                >
                    <Box
                      sx={{
                        position: 'relative',
                        pt: '100%',
                        bgcolor: '#0d1117',
                      }}
                    >
                      {canBatchSelect && (img || vid) && (
                        <Box
                          data-batch-checkbox
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBatchSelect(asset.id);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            zIndex: 2,
                            bgcolor: 'rgba(13,17,23,0.75)',
                            borderRadius: 1,
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={batchSelectedIds.includes(asset.id)}
                            sx={{ p: 0.5, color: '#8b949e' }}
                          />
                        </Box>
                      )}
                      {img && (
                        <CardMedia
                          component="img"
                          image={src}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            contentVisibility: 'auto',
                          }}
                        />
                      )}
                      {vid && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            bgcolor: '#010409',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}
                        >
                          {/* JPEG poster only — mounting <video> per card caused select/scroll lag. */}
                          {videoGridPosterPath(asset) ? (
                            <CardMedia
                              component="img"
                              image={publicFileUrl(videoGridPosterPath(asset))}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                              }}
                            />
                          ) : (
                            <Movie sx={{ fontSize: 48, color: '#30363d' }} />
                          )}
                          <Box
                            sx={{
                              position: 'absolute',
                              right: 8,
                              bottom: 8,
                              bgcolor: 'rgba(13,17,23,0.75)',
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.25,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              zIndex: 1,
                            }}
                          >
                            <Movie sx={{ fontSize: 16, color: '#58a6ff' }} />
                            <Typography
                              component="span"
                              sx={{ color: '#c9d1d9', fontSize: 11 }}
                            >
                              Video
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      {!img && !vid && (
                        <Typography
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%,-50%)',
                            color: '#8b949e',
                            fontSize: 12,
                          }}
                        >
                          {asset.file_type}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ p: 1 }}>
                      {!clientIdParam && asset._clientName && (
                        <Chip
                          label={asset._clientName}
                          size="small"
                          sx={{
                            mb: 0.5,
                            height: 20,
                            fontSize: 10,
                            maxWidth: '100%',
                          }}
                        />
                      )}
                      {(asset.watermark_job_status === 'queued' ||
                        asset.watermark_job_status === 'processing') && (
                        <Chip
                          label="Watermarking…"
                          size="small"
                          sx={{
                            mb: 0.5,
                            height: 22,
                            fontSize: 11,
                            bgcolor: '#1f3d5c',
                            color: '#79c0ff',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      )}
                      {asset.watermark_job_status === 'failed' && (
                        <Chip
                          label="Watermark failed"
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{
                            mb: 0.5,
                            height: 22,
                            fontSize: 11,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      )}
                      <Chip
                        label={`Watermark: ${watermarkStatusLabel(asset)}`}
                        size="small"
                        sx={{
                          mb: 0.75,
                          height: 22,
                          fontSize: 11,
                          maxWidth: '100%',
                          bgcolor: '#21262d',
                          color: '#c9d1d9',
                          '& .MuiChip-label': { px: 1, whiteSpace: 'normal' },
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: '#8b949e', display: 'block', mb: 0.75 }}
                      >
                        Caption: {asset.caption_status || '—'}
                      </Typography>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={0.5}
                        sx={{ mt: 0.5 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {assetNeedsCaptionGenerate(asset) && (
                          <Tooltip title="Primary: generate Gemini captions, then approve before promote">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={
                                  captionGenerateBusyId === asset.id ||
                                  captionBusy
                                }
                                onClick={(e) =>
                                  handleGenerateCaptionsForAsset(asset, e)
                                }
                                startIcon={
                                  captionGenerateBusyId === asset.id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <AutoAwesome sx={{ fontSize: 16 }} />
                                  )
                                }
                                sx={{
                                  fontSize: 11,
                                  py: 0.35,
                                  minWidth: 0,
                                  bgcolor: '#238636',
                                  '&:hover': { bgcolor: '#2ea043' },
                                }}
                              >
                                Generate caption
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {!assetNeedsCaptionGenerate(asset) &&
                          !asset.approved_caption?.caption &&
                          asset.client_id &&
                          (isImageMime(asset.file_type) ||
                            isVideoMime(asset.file_type)) && (
                            <Tooltip title="Open caption review — edit, regenerate, or approve">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={(e) =>
                                    handleOpenCaptionWorkflow(asset, e)
                                  }
                                  startIcon={<FactCheck sx={{ fontSize: 16 }} />}
                                  sx={{
                                    fontSize: 11,
                                    py: 0.35,
                                    minWidth: 0,
                                  }}
                                >
                                  Review captions
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        <Tooltip title="Download original file">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => handleDownloadOriginal(asset, e)}
                            startIcon={<CloudDownload sx={{ fontSize: 16 }} />}
                            sx={{
                              fontSize: 11,
                              py: 0.25,
                              minWidth: 0,
                              borderColor: '#30363d',
                              color: '#79c0ff',
                            }}
                          >
                            Original
                          </Button>
                        </Tooltip>
                        <Tooltip
                          title={
                            watermarkedDownloadPath(asset)
                              ? isVideoMime(asset.file_type) &&
                                !asset.watermarked_file_url &&
                                asset.video_preview_wm_file_url
                                ? 'Watermarked preview frame (JPEG)'
                                : 'Watermarked file'
                              : 'No watermarked file yet'
                          }
                        >
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={!watermarkedDownloadPath(asset)}
                              onClick={(e) => handleDownloadWatermarked(asset, e)}
                              startIcon={<CloudDownload sx={{ fontSize: 16 }} />}
                              sx={{
                                fontSize: 11,
                                py: 0.25,
                                minWidth: 0,
                                borderColor: '#30363d',
                                color: '#79c0ff',
                              }}
                            >
                              Watermarked
                            </Button>
                          </span>
                        </Tooltip>
                        {isImageMime(asset.file_type) && (
                          <Tooltip title="Apply or re-apply logo watermark">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                disabled={
                                  watermarkApplyBusyId === asset.id ||
                                  isWatermarkJobActive(asset)
                                }
                                onClick={(e) => handleApplyImageWatermark(asset, e)}
                                startIcon={
                                  watermarkApplyBusyId === asset.id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <Brush sx={{ fontSize: 16 }} />
                                  )
                                }
                                sx={{ fontSize: 11, py: 0.25, minWidth: 0 }}
                              >
                                Apply watermark
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {isVideoMime(asset.file_type) && (
                          <>
                            <Tooltip title="Watermarked still preview (JPEG)">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="secondary"
                                  disabled={
                                    watermarkApplyBusyId === asset.id ||
                                    isWatermarkJobActive(asset)
                                  }
                                  onClick={(e) =>
                                    handleApplyVideoPreviewWatermark(asset, e)
                                  }
                                  startIcon={
                                    watermarkApplyBusyId === asset.id ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <Brush sx={{ fontSize: 16 }} />
                                    )
                                  }
                                  sx={{ fontSize: 11, py: 0.25, minWidth: 0 }}
                                >
                                  WM preview
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip title="Encode full video with logo (slow)">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={
                                    watermarkApplyBusyId === asset.id ||
                                    isWatermarkJobActive(asset)
                                  }
                                  onClick={(e) =>
                                    handleApplyVideoFullWatermark(asset, e)
                                  }
                                  startIcon={
                                    watermarkApplyBusyId === asset.id ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <Brush sx={{ fontSize: 16 }} />
                                    )
                                  }
                                  sx={{ fontSize: 11, py: 0.25, minWidth: 0 }}
                                >
                                  WM full video
                                </Button>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                      {asset.approved_caption?.caption && (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ mt: 0.5 }}
                        >
                          <Chip
                            label="Approved for posts"
                            size="small"
                            color="success"
                            sx={{ height: 22, fontSize: 11 }}
                          />
                          {asset.linked_post_id ? (
                            <Chip
                              label={`Post #${asset.linked_post_id}`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 22, fontSize: 11 }}
                            />
                          ) : (
                            <Tooltip title="Create a draft in Posts with this caption, hashtags, and media">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={promoteBusyId === asset.id}
                                  onClick={(e) => handlePromoteToPost(asset, e)}
                                  startIcon={
                                    promoteBusyId === asset.id ? (
                                      <CircularProgress size={14} color="inherit" />
                                    ) : (
                                      <PostAdd sx={{ fontSize: 16 }} />
                                    )
                                  }
                                  sx={{ fontSize: 11, py: 0.25, minWidth: 0 }}
                                >
                                  Promote to post
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                        </Stack>
                      )}
                      {asset.source === 'scraped' && (
                        <Chip
                          label="Scraped"
                          size="small"
                          sx={{
                            mt: 0.5,
                            mr: 0.5,
                            height: 20,
                            fontSize: 10,
                            bgcolor: '#21262d',
                          }}
                        />
                      )}
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        {showArchived ? (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={archiveBusyId === asset.id}
                              onClick={(e) => handleUnarchiveAsset(asset, e)}
                              startIcon={<Unarchive sx={{ fontSize: 16 }} />}
                              sx={{ fontSize: 11, py: 0.25, minWidth: 0 }}
                            >
                              Restore
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={archiveBusyId === asset.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                setHardDeleteConfirm(asset);
                              }}
                              startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                              sx={{ fontSize: 11, py: 0.25, minWidth: 0 }}
                            >
                              Delete
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={archiveBusyId === asset.id}
                            onClick={(e) => handleArchiveAsset(asset, e)}
                            startIcon={<Archive sx={{ fontSize: 16 }} />}
                            sx={{
                              fontSize: 11,
                              py: 0.25,
                              minWidth: 0,
                              color: '#8b949e',
                              borderColor: '#30363d',
                            }}
                          >
                            Archive
                          </Button>
                        )}
                      </Stack>
                      {asset.source === 'post' && (
                        <Chip
                          label="From post"
                          size="small"
                          sx={{
                            mt: 0.5,
                            mr: 0.5,
                            height: 20,
                            fontSize: 10,
                            bgcolor: '#1f3d2f',
                          }}
                        />
                      )}
                      {asset.content_pillar && (
                        <Chip
                          label={
                            MEDIA_CONTENT_PILLAR_LABELS[asset.content_pillar] ||
                            asset.content_pillar
                          }
                          size="small"
                          sx={{ mt: 0.5, height: 22, fontSize: 11 }}
                        />
                      )}
                    </Box>
                  </Card>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog
        open={batchReviewOpen}
        onClose={() => setBatchReviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#161b22', border: '1px solid #30363d' } }}
      >
        <DialogTitle sx={{ color: '#f0f6fc' }}>
          Batch caption review
          <Typography
            component="div"
            variant="caption"
            sx={{ color: '#8b949e', display: 'block', mt: 0.5 }}
          >
            {batchSelectedIds.length} selected · Approve variant 1 for rows you
            check, then schedule approved items to the calendar (uses Promote
            draft as platform + stagger below).
          </Typography>
        </DialogTitle>
        <DialogContent>
          {batchReviewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{ bgcolor: '#0d1117', border: '1px solid #30363d' }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#8b949e', width: 48 }}>
                      Approve
                    </TableCell>
                    <TableCell sx={{ color: '#8b949e' }}>Asset</TableCell>
                    <TableCell sx={{ color: '#8b949e' }}>
                      First variant (preview)
                    </TableCell>
                    <TableCell sx={{ color: '#8b949e', width: 120 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ color: '#8b949e', width: 100 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batchSelectedIds.map((id) => {
                    const asset = items.find((a) => a.id === id);
                    const pack = batchReviewByAssetId[id];
                    const drafts = pack?.drafts || [];
                    const ready =
                      drafts.find((d) => d.status === 'ready') || drafts[0];
                    const v0 = ready?.variants?.[0];
                    const cap = (v0?.caption && String(v0.caption)) || '';
                    const previewText = cap
                      ? cap.length > 220
                        ? `${cap.slice(0, 220)}…`
                        : cap
                      : '—';
                    const canApprove =
                      ready?.status === 'ready' && cap.trim().length > 0;
                    return (
                      <TableRow key={id}>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={batchReviewApproveIds.includes(id)}
                            disabled={!canApprove}
                            onChange={() => toggleBatchReviewApprove(id)}
                            sx={{ color: '#8b949e' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#c9d1d9', maxWidth: 160 }}>
                          {asset ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              {isImageMime(asset.file_type) ? (
                                <Box
                                  component="img"
                                  src={publicFileUrl(asset.file_url)}
                                  alt=""
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    objectFit: 'cover',
                                    borderRadius: 0.5,
                                    bgcolor: '#010409',
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 0.5,
                                    bgcolor: '#010409',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Movie sx={{ color: '#58a6ff', fontSize: 28 }} />
                                </Box>
                              )}
                              <Typography variant="caption" noWrap title={asset.asset_id}>
                                {asset.asset_id}
                              </Typography>
                            </Stack>
                          ) : (
                            `#${id}`
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: '#8b949e',
                            whiteSpace: 'pre-wrap',
                            maxWidth: 360,
                          }}
                        >
                          {pack?.error || previewText}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {asset?.approved_caption?.caption ? (
                            <Chip
                              label="Approved"
                              size="small"
                              sx={{ bgcolor: '#1f3d2f', color: '#3fb950' }}
                            />
                          ) : ready?.status === 'ready' ? (
                            <Chip
                              label="Draft ready"
                              size="small"
                              sx={{ bgcolor: '#21262d', color: '#8b949e' }}
                            />
                          ) : (
                            <Chip
                              label="No draft"
                              size="small"
                              sx={{ bgcolor: '#3d1f1f', color: '#f85149' }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {asset && (
                            <Button
                              size="small"
                              onClick={() => {
                                setBatchReviewOpen(false);
                                setPreview(asset);
                              }}
                              sx={{ color: '#58a6ff' }}
                            >
                              Open
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            flexWrap: 'wrap',
            gap: 1,
            px: 3,
            pb: 2,
            borderTop: '1px solid #30363d',
          }}
        >
          <TextField
            size="small"
            label="Stagger (minutes)"
            type="number"
            value={batchStaggerMinutes}
            onChange={(e) => setBatchStaggerMinutes(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{
              width: 140,
              '& .MuiInputLabel-root': { color: '#8b949e' },
              '& .MuiOutlinedInput-input': { color: '#f0f6fc' },
            }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            onClick={() => setBatchReviewOpen(false)}
            sx={{ color: '#8b949e' }}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<FactCheck />}
            disabled={
              !batchReviewApproveIds.length || batchApproveBusy || !batchClientId
            }
            onClick={handleBatchApproveChecked}
            sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
          >
            Approve checked
          </Button>
          <Button
            variant="contained"
            startIcon={<CalendarMonth />}
            disabled={
              !scheduleEligibleIds.length ||
              batchScheduleBusy ||
              !batchClientId
            }
            onClick={handleBatchScheduleToCalendar}
            sx={{ bgcolor: '#8957e5', '&:hover': { bgcolor: '#a371f7' } }}
          >
            Schedule approved ({scheduleEligibleIds.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!preview}
        onClose={handleClosePreview}
        maxWidth={
          preview &&
          preview.client_id &&
          (isImageAsset(preview) || isVideoAsset(preview))
            ? 'xl'
            : 'md'
        }
        fullWidth
        PaperProps={{ sx: { bgcolor: '#161b22', border: '1px solid #30363d' } }}
      >
        {preview && (
          <>
            <DialogTitle sx={{ color: '#f0f6fc' }}>
              {preview.asset_id}
              <Typography
                component="div"
                variant="caption"
                sx={{ color: '#8b949e', display: 'block' }}
              >
                {preview._clientName ||
                  clients.find((c) => c.id === preview.client_id)?.name ||
                  `Client ${preview.client_id}`}{' '}
                · {preview.file_type} · {preview.source}
                {preview.tags?.instagram_post_id
                  ? ` · IG ${preview.tags.instagram_post_id}`
                  : ''}
                <br />
                {preview.tags?.original_url || preview.file_url}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {isImageAsset(preview) && (
                  <Box
                    component="img"
                    src={publicFileUrl(preview.file_url)}
                    alt=""
                    sx={{ maxWidth: '100%', maxHeight: '55vh', borderRadius: 1 }}
                  />
                )}
                {isVideoAsset(preview) && (
                  <Box
                    component="video"
                    src={publicFileUrl(preview.file_url)}
                    controls
                    sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1 }}
                  />
                )}
                {!isImageAsset(preview) &&
                  !isVideoAsset(preview) && (
                    <Typography sx={{ color: '#8b949e' }}>
                      Preview not available for this type.
                    </Typography>
                  )}
              </Box>

              {(isImageAsset(preview) || isVideoAsset(preview)) &&
                preview.client_id && (
                  <Box
                    ref={captionWorkflowRef}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: '#0d1117',
                      border: '1px solid #238636',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      justifyContent="space-between"
                      sx={{ width: '100%' }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ color: '#f0f6fc', fontWeight: 700, mb: 0.75 }}
                        >
                          Caption workflow
                        </Typography>
                        <Stack spacing={0.35} sx={{ mb: 1.25 }}>
                          {captionWorkflowChecklist(preview, activeDraft).map(
                            (step) => (
                              <Typography
                                key={step.id}
                                variant="body2"
                                sx={{
                                  color: step.done ? '#3fb950' : '#8b949e',
                                }}
                              >
                                {step.done ? '✓' : '○'} {step.label}
                                {step.hint ? ` — ${step.hint}` : ''}
                              </Typography>
                            ),
                          )}
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#6e7681' }}>
                          Approve or edit a variant below before Promote. Regenerate anytime.
                        </Typography>
                      </Box>
                      {assetNeedsCaptionGenerate(preview) && (
                        <Button
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={
                            captionBusy ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <AutoAwesome />
                            )
                          }
                          disabled={captionBusy}
                          onClick={handleGenerateOrRegenerateCaptions}
                          sx={{
                            bgcolor: '#238636',
                            '&:hover': { bgcolor: '#2ea043' },
                            flexShrink: 0,
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          Generate caption
                        </Button>
                      )}
                      {!assetNeedsCaptionGenerate(preview) &&
                        !preview.approved_caption?.caption && (
                          <Button
                            variant="outlined"
                            size="large"
                            startIcon={<AutoAwesome />}
                            disabled={captionBusy}
                            onClick={handleGenerateOrRegenerateCaptions}
                            sx={{
                              flexShrink: 0,
                              minHeight: 44,
                              width: { xs: '100%', sm: 'auto' },
                            }}
                          >
                            Regenerate
                          </Button>
                        )}
                      {preview.approved_caption?.caption &&
                        !preview.linked_post_id && (
                          <Button
                            variant="contained"
                            size="large"
                            color="primary"
                            startIcon={
                              promoteBusyId === preview.id ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <PostAdd />
                              )
                            }
                            disabled={promoteBusyId === preview.id}
                            onClick={(e) => handlePromoteToPost(preview, e)}
                            sx={{ flexShrink: 0 }}
                          >
                            Promote to post
                          </Button>
                        )}
                      {showArchived ? (
                        <>
                          <Button
                            variant="outlined"
                            size="large"
                            startIcon={
                              archiveBusyId === preview.id ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <Unarchive />
                              )
                            }
                            disabled={archiveBusyId === preview.id}
                            onClick={(e) => handleUnarchiveAsset(preview, e)}
                            sx={{ flexShrink: 0 }}
                          >
                            Restore
                          </Button>
                          <Button
                            variant="outlined"
                            size="large"
                            color="error"
                            startIcon={<DeleteOutline />}
                            disabled={archiveBusyId === preview.id}
                            onClick={() => setHardDeleteConfirm(preview)}
                            sx={{ flexShrink: 0 }}
                          >
                            Delete permanently
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={
                            archiveBusyId === preview.id ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <Archive />
                            )
                          }
                          disabled={archiveBusyId === preview.id}
                          onClick={(e) => handleArchiveAsset(preview, e)}
                          sx={{ flexShrink: 0, color: '#8b949e' }}
                        >
                          Archive
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}

              {preview.client_id &&
                (isImageAsset(preview) || isVideoAsset(preview)) && (
                  <>
                    <Divider sx={{ borderColor: '#30363d', mb: 2 }} />
                    <Typography
                      variant="subtitle2"
                      sx={{ color: '#8b949e', mb: 1, fontWeight: 600 }}
                    >
                      Watermark (optional)
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#8b949e', mb: 1.5 }}
                    >
                      Status: {watermarkStatusLabel(preview)}
                    </Typography>
                    <Stack
                      direction="row"
                      flexWrap="wrap"
                      gap={1}
                      sx={{ mb: 1 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        onClick={(e) => handleDownloadOriginal(preview, e)}
                        sx={{ borderColor: '#30363d', color: '#79c0ff' }}
                      >
                        Download original
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!watermarkedDownloadPath(preview)}
                        startIcon={<CloudDownload />}
                        onClick={(e) => handleDownloadWatermarked(preview, e)}
                        sx={{ borderColor: '#30363d', color: '#79c0ff' }}
                      >
                        Download watermarked
                        {isVideoMime(preview.file_type) &&
                        !preview.watermarked_file_url &&
                        preview.video_preview_wm_file_url
                          ? ' (preview)'
                          : ''}
                      </Button>
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {isImageMime(preview.file_type) && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            watermarkApplyBusyId === preview.id ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <Brush />
                            )
                          }
                          disabled={
                            watermarkApplyBusyId === preview.id ||
                            isWatermarkJobActive(preview)
                          }
                          onClick={(e) => handleApplyImageWatermark(preview, e)}
                        >
                          Apply watermark
                        </Button>
                      )}
                      {isVideoMime(preview.file_type) && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            startIcon={
                              watermarkApplyBusyId === preview.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <Brush />
                              )
                            }
                            disabled={
                              watermarkApplyBusyId === preview.id ||
                              isWatermarkJobActive(preview)
                            }
                            onClick={(e) =>
                              handleApplyVideoPreviewWatermark(preview, e)
                            }
                          >
                            Apply preview (frame)
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={
                              watermarkApplyBusyId === preview.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <Brush />
                              )
                            }
                            disabled={
                              watermarkApplyBusyId === preview.id ||
                              isWatermarkJobActive(preview)
                            }
                            onClick={(e) =>
                              handleApplyVideoFullWatermark(preview, e)
                            }
                          >
                            Apply full video
                          </Button>
                        </>
                      )}
                    </Stack>
                  </>
                )}

              {(isImageAsset(preview) || isVideoAsset(preview)) &&
                preview.client_id && (
                <>
                  <Divider sx={{ borderColor: '#30363d', mb: 2 }} />
                    <GalleryPostIntentField
                      assetId={preview.id}
                      clientId={preview.client_id}
                      initialValue={captionPostIntent}
                      saving={captionIntentSaving}
                      onLocalChange={setCaptionPostIntent}
                      onSave={handleSaveCaptionPostIntent}
                      showError={showError}
                      showSuccess={showSuccess}
                    />
                    <GalleryTargetPlatformsField
                      assetId={preview.id}
                      options={GALLERY_PROMOTE_PLATFORM_OPTIONS}
                      initialValue={assetTargetPlatforms}
                      defaultValue={GALLERY_PROMOTE_DEFAULT}
                      saving={assetPlatformsSaving}
                      onSave={handleSaveAssetTargetPlatforms}
                    />
                    {(assetTargetPlatforms || []).includes('youtube') && (
                      <GalleryYouTubeSettingsField
                        assetId={preview.id}
                        clientId={preview.client_id}
                        initialValue={youtubeSettings}
                        saving={youtubeSettingsSaving}
                        onSave={handleSaveYoutubeSettings}
                      />
                    )}
                  <Divider sx={{ borderColor: '#30363d', mb: 2 }} />
                  <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: '#f0f6fc', fontWeight: 600 }}>
                      Caption review — approve, edit, or regenerate
                    </Typography>
                    {isVideoMime(preview.file_type) && (
                      <Typography variant="caption" sx={{ color: '#6e7681' }}>
                        Video (v1): captions from 3 ffmpeg frames + Gemini Vision
                      </Typography>
                    )}
                  </Stack>
                  {captionDraftsLoading && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 3,
                      }}
                    >
                      <CircularProgress size={32} />
                    </Box>
                  )}
                  {!captionDraftsLoading && !activeDraft && (
                    <Stack spacing={1.5} alignItems="flex-start">
                      <Typography sx={{ color: '#8b949e' }}>
                        No caption drafts yet. Use the primary <strong>Generate caption</strong>{' '}
                        button above, or generate here.
                      </Typography>
                      <Button
                        variant="contained"
                        size="medium"
                        startIcon={<AutoAwesome />}
                        disabled={captionBusy}
                        onClick={handleGenerateOrRegenerateCaptions}
                        sx={{ bgcolor: '#238636', '&:hover': { bgcolor: '#2ea043' } }}
                      >
                        Generate caption
                      </Button>
                    </Stack>
                  )}
                  {!captionDraftsLoading &&
                    activeDraft &&
                    activeDraft.status === 'failed' && (
                      <Stack spacing={1.5} alignItems="flex-start">
                        <Typography sx={{ color: '#f85149' }}>
                          {activeDraft.error_message ||
                            'Caption generation failed — try again'}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AutoAwesome />}
                          disabled={captionBusy}
                          onClick={handleGenerateOrRegenerateCaptions}
                        >
                          Regenerate
                        </Button>
                      </Stack>
                    )}
                  {!captionDraftsLoading &&
                    activeDraft &&
                    activeDraft.status === 'ready' &&
                    !localVariants && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          py: 2,
                        }}
                      >
                        <CircularProgress size={28} />
                      </Box>
                    )}
                  {!captionDraftsLoading &&
                    activeDraft &&
                    activeDraft.status === 'ready' &&
                    localVariants && (
                      <>
                        <Typography
                          variant="caption"
                          sx={{ color: '#6e7681', display: 'block', mb: 1.5 }}
                        >
                          Task 5.7 / W3.F2: ML engagement scores (0–100) use the same model as
                          pre-publish scoring; hover a score for concrete improvement tips
                          (Class B estimates). They refresh ~1s after you stop editing.
                          Platform matches &quot;Promote draft as&quot; in the toolbar.
                        </Typography>
                        {variantMlScores?._error && (
                          <Alert severity="warning" sx={{ mb: 2, bgcolor: '#3d2f00' }}>
                            {variantMlScores._error}
                          </Alert>
                        )}
                        {activeDraft?.pre_review && (
                          <Alert
                            severity={
                              activeDraft.pre_review.action === 'flag'
                                ? 'warning'
                                : 'success'
                            }
                            sx={{
                              mb: 2,
                              bgcolor:
                                activeDraft.pre_review.action === 'flag'
                                  ? '#3d2810'
                                  : '#0d2818',
                              '& .MuiAlert-message': { width: '100%' },
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: '#f0f6fc' }}
                            >
                              Pre-review{' '}
                              {activeDraft.pre_review.action === 'flag'
                                ? '— review suggested'
                                : 'passed'}
                              {(activeDraft.pre_review.attempts_used || 0) > 1
                                ? ` · ${activeDraft.pre_review.attempts_used} generation attempt(s)`
                                : ''}
                            </Typography>
                            {activeDraft.pre_review.action === 'flag' &&
                              Array.isArray(activeDraft.pre_review.reasons) &&
                              activeDraft.pre_review.reasons.length > 0 && (
                                <Box
                                  component="ul"
                                  sx={{
                                    m: 0,
                                    mt: 1,
                                    pl: 2,
                                    color: '#d29922',
                                    fontSize: '0.8125rem',
                                  }}
                                >
                                  {activeDraft.pre_review.reasons
                                    .slice(0, 12)
                                    .map((r, i) => (
                                      <li key={i}>{r}</li>
                                    ))}
                                </Box>
                              )}
                            {activeDraft.pre_review.brand_voice_rationale && (
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', mt: 1, color: '#8b949e' }}
                              >
                                Voice check: {activeDraft.pre_review.brand_voice_rationale}
                              </Typography>
                            )}
                          </Alert>
                        )}
                        {captionEditSuggestions?.enabled &&
                          captionEditSuggestions?.meets_threshold &&
                          Array.isArray(captionEditSuggestions.suggestions) &&
                          captionEditSuggestions.suggestions.length > 0 && (
                          <Alert
                            severity="info"
                            icon={false}
                            sx={{
                              mb: 2,
                              bgcolor: '#0d1117',
                              border: '1px solid #238636',
                              color: '#c9d1d9',
                              '& .MuiAlert-message': { width: '100%' },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                              sx={{ mb: 1 }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600, color: '#3fb950' }}
                              >
                                Suggestions based on your edits
                              </Typography>
                              <MlEstimateBadge basis="caption_edit_learning" />
                            </Stack>
                            <Typography variant="caption" sx={{ color: '#8b949e', display: 'block', mb: 1 }}>
                              {captionEditSuggestions.genuine_pair_count != null
                                ? `${captionEditSuggestions.genuine_pair_count} confirmed edit pairs · `
                                : ''}
                              Gemini will prefer these phrasing patterns on the next generate. Opt out in Brand &amp; AI onboarding.
                            </Typography>
                            {(captionEditSuggestions.patterns?.preferred_hashtags?.length > 0 ||
                              captionEditSuggestions.patterns?.vocabulary?.length > 0 ||
                              captionEditSuggestions.patterns?.tone) && (
                              <Box sx={{ mb: 1.25 }}>
                                {captionEditSuggestions.patterns?.tone && (
                                  <Typography variant="caption" sx={{ color: '#8b949e', display: 'block' }}>
                                    Tone: {captionEditSuggestions.patterns.tone}
                                  </Typography>
                                )}
                                {captionEditSuggestions.patterns?.preferred_hashtags?.length > 0 && (
                                  <Typography variant="caption" sx={{ color: '#8b949e', display: 'block' }}>
                                    Hashtags you add:{' '}
                                    {captionEditSuggestions.patterns.preferred_hashtags
                                      .map((t) => `#${t}`)
                                      .join(' ')}
                                  </Typography>
                                )}
                                {captionEditSuggestions.patterns?.vocabulary?.length > 0 && (
                                  <Typography variant="caption" sx={{ color: '#8b949e', display: 'block' }}>
                                    Words you use: {captionEditSuggestions.patterns.vocabulary.slice(0, 8).join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            <Stack spacing={1.25}>
                              {captionEditSuggestions.suggestions.slice(0, 4).map((s) => (
                                <Box key={s.id}>
                                  <Typography variant="body2" sx={{ color: '#f0f6fc', fontWeight: 600 }}>
                                    Prefer: {s.edited_caption}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#8b949e' }}>
                                    instead of: {s.original_caption}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>
                          </Alert>
                        )}
                        {activeDraft?.why_this &&
                          (activeDraft.why_this.summary ||
                            (Array.isArray(activeDraft.why_this.patterns) &&
                              activeDraft.why_this.patterns.length > 0)) && (
                          <Alert
                            severity="info"
                            icon={false}
                            sx={{
                              mb: 2,
                              bgcolor: '#0d1117',
                              border: '1px solid #30363d',
                              color: '#c9d1d9',
                              '& .MuiAlert-message': { width: '100%' },
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: '#79c0ff', mb: 1 }}
                            >
                              Why this draft
                            </Typography>
                            {activeDraft.why_this.summary ? (
                              <Typography
                                variant="body2"
                                sx={{ mb: 1.5, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                              >
                                {activeDraft.why_this.summary}
                              </Typography>
                            ) : null}
                            {Array.isArray(activeDraft.why_this.patterns) &&
                              activeDraft.why_this.patterns.length > 0 && (
                                <Box
                                  component="ul"
                                  sx={{
                                    m: 0,
                                    pl: 2.25,
                                    color: '#8b949e',
                                    fontSize: '0.8125rem',
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {activeDraft.why_this.patterns.map((p, i) => (
                                    <li key={i}>{p.line || p.label || '—'}</li>
                                  ))}
                                </Box>
                              )}
                            {activeDraft.why_this.pillar_scope === 'pillar' &&
                            activeDraft.why_this.media_pillar ? (
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', mt: 1, color: '#6e7681' }}
                              >
                                Competitor patterns scoped to the &quot;
                                {activeDraft.why_this.media_pillar}&quot; pillar where data
                                allows.
                              </Typography>
                            ) : null}
                          </Alert>
                        )}
                        <Grid container spacing={2}>
                          {localVariants.map((v, idx) => {
                            const pv = (activeDraft.pre_review?.variants || [])[idx];
                            return (
                            <Grid item xs={12} md={4} key={`${v.style}-${idx}`}>
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: 1,
                                  border: '1px solid #30363d',
                                  bgcolor: '#0d1117',
                                  height: '100%',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ mb: 1 }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 600, color: '#f0f6fc' }}
                                  >
                                    {(v.style || '').replace(/_/g, ' ')}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    useFlexGap
                                  >
                                    {variantMlLoading &&
                                      !(variantMlScores?.scores || []).find(
                                        (s) => s.variant_index === idx,
                                      ) && (
                                        <CircularProgress size={14} thickness={5} />
                                      )}
                                    {(() => {
                                      const ml = (
                                        variantMlScores?.scores || []
                                      ).find((s) => s.variant_index === idx);
                                      if (!ml) return null;
                                      const lines = [];
                                      if (ml.predicted_engagement != null) {
                                        lines.push(
                                          `Predicted engagement (model units): ${ml.predicted_engagement}`,
                                        );
                                      }
                                      const tips = (ml.suggestions || [])
                                        .map((s) => (s && s.text) || '')
                                        .filter(Boolean);
                                      if (tips.length) {
                                        lines.push('How to improve (model estimate):');
                                        tips.slice(0, 4).forEach((t, i) => {
                                          lines.push(`${i + 1}. ${t}`);
                                        });
                                      } else {
                                        (ml.signals || []).forEach((sig) => {
                                          lines.push(`Signal: ${sig}`);
                                        });
                                      }
                                      if (ml.model_error) {
                                        lines.push(ml.model_error);
                                      }
                                      const tip = lines.join('\n');
                                      const label =
                                        ml.predicted_score != null
                                          ? `ML ${ml.predicted_score}`
                                          : ml.model_error
                                            ? 'ML n/a'
                                            : null;
                                      if (!label) return null;
                                      return (
                                        <Tooltip
                                          title={tip}
                                          placement="top"
                                          slotProps={{
                                            tooltip: {
                                              sx: {
                                                maxWidth: 360,
                                                whiteSpace: 'pre-wrap',
                                              },
                                            },
                                          }}
                                        >
                                          <Chip
                                            size="small"
                                            label={label}
                                            sx={{
                                              bgcolor: '#3d2f00',
                                              color: '#d29922',
                                              fontWeight: 600,
                                            }}
                                          />
                                        </Tooltip>
                                      );
                                    })()}
                                    <Chip
                                      size="small"
                                      label={v.review_status || 'draft'}
                                      color={
                                        v.review_status === 'approved'
                                          ? 'success'
                                          : v.review_status === 'used'
                                            ? 'info'
                                            : 'default'
                                      }
                                      sx={{
                                        textTransform: 'capitalize',
                                        '& .MuiChip-label': { px: 1 },
                                      }}
                                    />
                                  </Stack>
                                </Stack>
                                {pv && (
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ mb: 1 }}
                                  >
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label="Similarity"
                                      color={
                                        pv.similarity?.ok ? 'success' : 'warning'
                                      }
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label="Policy"
                                      color={
                                        pv.policy?.ok ? 'success' : 'error'
                                      }
                                    />
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label="Voice"
                                      color={
                                        pv.brand_voice?.skipped
                                          ? 'default'
                                          : pv.brand_voice?.ok
                                            ? 'success'
                                            : 'warning'
                                      }
                                    />
                                  </Stack>
                                )}
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Title"
                                  value={v.title}
                                  onChange={(e) =>
                                    updateVariantField(
                                      idx,
                                      'title',
                                      e.target.value,
                                    )
                                  }
                                  sx={{
                                    mb: 1,
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: '#161b22',
                                    },
                                    '& .MuiInputLabel-root': { color: '#8b949e' },
                                    '& .MuiOutlinedInput-input': {
                                      color: '#f0f6fc',
                                    },
                                  }}
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Caption"
                                  multiline
                                  minRows={4}
                                  value={v.caption}
                                  onChange={(e) =>
                                    updateVariantField(
                                      idx,
                                      'caption',
                                      e.target.value,
                                    )
                                  }
                                  sx={{
                                    mb: 1,
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: '#161b22',
                                    },
                                    '& .MuiInputLabel-root': { color: '#8b949e' },
                                    '& .MuiOutlinedInput-input': {
                                      color: '#f0f6fc',
                                    },
                                  }}
                                />
                                <TextField
                                  fullWidth
                                  size="small"
                                  label="Hashtags (comma-separated)"
                                  value={v.hashtags}
                                  onChange={(e) =>
                                    updateVariantField(
                                      idx,
                                      'hashtags',
                                      e.target.value,
                                    )
                                  }
                                  helperText={
                                    activeDraft?.hashtag_strategy
                                      ? 'Seeded from hashtag strategy — edit anytime before Approve'
                                      : undefined
                                  }
                                  FormHelperTextProps={{
                                    sx: { color: '#6e7681' },
                                  }}
                                  sx={{
                                    mb: 1.5,
                                    '& .MuiOutlinedInput-root': {
                                      bgcolor: '#161b22',
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: '#8b949e',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                      color: '#f0f6fc',
                                    },
                                  }}
                                />
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  <Button
                                    size="large"
                                    variant="contained"
                                    color="success"
                                    disabled={captionBusy}
                                    onClick={() => handleApproveVariant(idx)}
                                    sx={{
                                      minHeight: 44,
                                      minWidth: { xs: '100%', sm: 120 },
                                    }}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="large"
                                    variant="outlined"
                                    disabled={
                                      captionBusy ||
                                      v.review_status !== 'approved'
                                    }
                                    onClick={() => handleMarkVariantUsed(idx)}
                                    sx={{
                                      minHeight: 44,
                                      minWidth: { xs: '100%', sm: 120 },
                                    }}
                                  >
                                    Mark used
                                  </Button>
                                </Stack>
                              </Box>
                            </Grid>
                            );
                          })}
                        </Grid>
                        <Divider sx={{ borderColor: '#30363d', my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#8b949e', mb: 1 }}
                        >
                          Platform-formatted preview (Task 5.2 — uses AI profile
                          platform order)
                        </Typography>
                        <Tabs
                          value={formatPreviewTab}
                          onChange={(_, v) => setFormatPreviewTab(v)}
                          sx={{
                            minHeight: 36,
                            '& .MuiTab-root': {
                              color: '#8b949e',
                              minHeight: 36,
                              py: 0.5,
                            },
                            '& .Mui-selected': { color: '#58a6ff' },
                          }}
                        >
                          {formatPlatformOrder.map((pk) => (
                            <Tab
                              key={pk}
                              label={
                                pk === 'instagram'
                                  ? 'Instagram'
                                  : pk === 'facebook'
                                    ? 'Facebook'
                                    : 'LinkedIn'
                              }
                            />
                          ))}
                        </Tabs>
                        {formatPreviewLoading && (
                          <Box sx={{ py: 1 }}>
                            <LinearProgress />
                          </Box>
                        )}
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          {localVariants.map((v, idx) => {
                            const fr = formatPreviewRows?.[idx];
                            return (
                              <Grid
                                item
                                xs={12}
                                md={4}
                                key={`fmt-${v.style}-${idx}`}
                              >
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    border: '1px solid #21262d',
                                    bgcolor: '#0d1117',
                                    height: '100%',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: '#8b949e',
                                      display: 'block',
                                      mb: 1,
                                    }}
                                  >
                                    {(v.style || '').replace(/_/g, ' ')} ·{' '}
                                    {formatPreviewPlatform}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: '#c9d1d9',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {fr?.full_text ||
                                      (formatPreviewLoading ? '…' : '—')}
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          sx={{ mt: 2, flexWrap: 'wrap' }}
                          useFlexGap
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={captionBusy}
                            onClick={handleSaveCaptionDraft}
                          >
                            Save changes
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AutoAwesome />}
                            disabled={captionBusy}
                            onClick={handleGenerateOrRegenerateCaptions}
                          >
                            Regenerate captions
                          </Button>
                        </Stack>
                        <Divider sx={{ borderColor: '#30363d', my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#f0f6fc', mb: 0.5, fontWeight: 600 }}
                        >
                          Draft package (1.6 — approve to queue)
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#6e7681', display: 'block', mb: 1.5 }}
                        >
                          One action approves asset + approved caption + proposed time. This step
                          does not schedule. Use Promote or batch schedule when ready.
                        </Typography>
                        <Stack
                          direction="row"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={1}
                          sx={{ mb: 1.5 }}
                        >
                          <Chip
                            size="small"
                            label={
                              preview.draft_gate_status === 'ready_to_schedule'
                                ? 'Ready to schedule'
                                : preview.draft_gate_status === 'rejected'
                                  ? 'Package rejected'
                                  : 'Not in queue'
                            }
                            color={
                              preview.draft_gate_status === 'ready_to_schedule'
                                ? 'success'
                                : preview.draft_gate_status === 'rejected'
                                  ? 'error'
                                  : 'default'
                            }
                            sx={
                              preview.draft_gate_status
                                ? {}
                                : { color: '#8b949e', borderColor: '#30363d' }
                            }
                            variant={
                              preview.draft_gate_status ? 'filled' : 'outlined'
                            }
                          />
                          {(preview.draft_gate_status === 'ready_to_schedule' ||
                            preview.draft_gate_status === 'rejected') && (
                            <Button
                              size="small"
                              variant="text"
                              disabled={draftGateBusy}
                              onClick={handleWithdrawDraftPackage}
                              sx={{ color: '#8b949e' }}
                            >
                              Withdraw gate
                            </Button>
                          )}
                        </Stack>
                        {preview.draft_gate_status === 'rejected' &&
                          preview.draft_gate?.rejection_reason && (
                            <Alert severity="warning" sx={{ mb: 1.5, bgcolor: '#3d2810' }}>
                              <Typography variant="body2" sx={{ color: '#f0f6fc' }}>
                                Last rejection: {preview.draft_gate.rejection_reason}
                              </Typography>
                              {(preview.draft_gate.brief_amendment || '').trim() ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#8b949e', display: 'block', mt: 0.5 }}
                                >
                                  Amendment stored — Regenerate captions applies it automatically.
                                </Typography>
                              ) : null}
                            </Alert>
                          )}
                        {!preview.approved_caption?.caption && (
                          <Alert severity="info" sx={{ mb: 1.5, bgcolor: '#1c2128' }}>
                            Approve one caption variant above before you can approve the draft
                            package.
                          </Alert>
                        )}
                        <TextField
                          fullWidth
                          size="small"
                          type="datetime-local"
                          label="Proposed publish time"
                          value={proposedPublishLocal}
                          onChange={(e) => setProposedPublishLocal(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          disabled={draftGateBusy}
                          sx={{
                            mb: 1.5,
                            maxWidth: 320,
                            '& .MuiOutlinedInput-root': { bgcolor: '#161b22' },
                            '& .MuiInputLabel-root': { color: '#8b949e' },
                            '& .MuiOutlinedInput-input': { color: '#f0f6fc' },
                          }}
                        />
                        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            disabled={
                              draftGateBusy ||
                              !preview.approved_caption?.caption ||
                              !proposedPublishLocal
                            }
                            onClick={handleApproveDraftPackage}
                          >
                            Approve draft package
                          </Button>
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{ color: '#6e7681', display: 'block', mb: 1 }}
                        >
                          Reject sends feedback for the next regeneration (optional brief
                          amendment).
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          label="Rejection reason"
                          value={draftGateRejectReason}
                          onChange={(e) => setDraftGateRejectReason(e.target.value)}
                          disabled={draftGateBusy}
                          sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': { bgcolor: '#161b22' },
                            '& .MuiInputLabel-root': { color: '#8b949e' },
                            '& .MuiOutlinedInput-input': { color: '#f0f6fc' },
                          }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Brief amendment (optional, for regenerate)"
                          value={draftGateRejectAmendment}
                          onChange={(e) => setDraftGateRejectAmendment(e.target.value)}
                          disabled={draftGateBusy}
                          multiline
                          minRows={2}
                          sx={{
                            mb: 1.5,
                            '& .MuiOutlinedInput-root': { bgcolor: '#161b22' },
                            '& .MuiInputLabel-root': { color: '#8b949e' },
                            '& .MuiOutlinedInput-input': { color: '#f0f6fc' },
                          }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          disabled={draftGateBusy}
                          onClick={handleRejectDraftPackage}
                        >
                          Reject draft package
                        </Button>
                        <Divider sx={{ borderColor: '#30363d', my: 2 }} />
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#f0f6fc', mb: 0.5, fontWeight: 600 }}
                        >
                          Schedule with ML (2.1 &amp; 2.2)
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: '#6e7681', display: 'block', mb: 1.5 }}
                        >
                          Preview shows the proposed time, source, and rationale. Drag to
                          nudge the slot, accept one-click, or pick manually — then commit.
                          Promotes to post + schedules.
                        </Typography>
                        {!mlSlotPreview && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={
                              mlSlotLoading ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <CalendarMonth />
                              )
                            }
                            disabled={
                              mlSlotLoading ||
                              !preview.approved_caption?.caption ||
                              !!preview.linked_post_id
                            }
                            onClick={handleScheduleWithMlPreview}
                            sx={{
                              bgcolor: '#8957e5',
                              '&:hover': { bgcolor: '#a371f7' },
                              mb: 2,
                            }}
                          >
                            Schedule with ML
                          </Button>
                        )}
                        {mlSlotPreview && (
                          <Box
                            sx={{
                              mb: 2,
                              p: 2,
                              border: '1px solid #30363d',
                              borderRadius: 1,
                              bgcolor: '#0d1117',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ color: '#f0f6fc', mb: 1, fontWeight: 600 }}
                            >
                              Slot preview
                            </Typography>
                            <Stack
                              direction="row"
                              flexWrap="wrap"
                              gap={1}
                              sx={{ mb: 1 }}
                            >
                              <Chip
                                size="small"
                                label={mlSlotPreview.source_key || mlSlotPreview.source}
                                sx={{
                                  fontFamily: 'ui-monospace, monospace',
                                  fontSize: 12,
                                  bgcolor: '#21262d',
                                  color: '#79c0ff',
                                }}
                              />
                              <Chip
                                size="small"
                                label={
                                  mlSlotPreview.source_label ||
                                  (mlSlotPreview.source === 'top_performers'
                                    ? 'Top performer aggregates'
                                    : 'Peak heuristic')
                                }
                                color={
                                  mlSlotPreview.source === 'top_performers'
                                    ? 'success'
                                    : 'default'
                                }
                              />
                            </Stack>
                            <Typography
                              variant="caption"
                              sx={{ color: '#6e7681', display: 'block', mb: 1 }}
                            >
                              Proposed time
                              {mlSlotPreview.scheduling_timezone
                                ? ` · ${mlSlotPreview.scheduling_timezone}`
                                : ''}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#c9d1d9', mb: 1.5, fontWeight: 500 }}
                            >
                              {mlSlotMode === 'suggested' && mlSlotEffectiveIso
                                ? formatMlSlotEt(mlSlotEffectiveIso)
                                : mlSlotPreview.suggested_time_display_et ||
                                  formatMlSlotEt(mlSlotPreview.suggested_time)}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#8b949e',
                                display: 'block',
                                mb: 1.5,
                                lineHeight: 1.45,
                              }}
                            >
                              {mlSlotPreview.rationale}
                            </Typography>
                            <Stack spacing={1} sx={{ mb: 1.5 }}>
                              <label>
                                <input
                                  type="radio"
                                  checked={mlSlotMode === 'suggested'}
                                  onChange={() => setMlSlotMode('suggested')}
                                />{' '}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{ color: '#c9d1d9' }}
                                >
                                  Use ML suggestion (drag to adjust)
                                </Typography>
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  checked={mlSlotMode === 'manual'}
                                  onChange={() => setMlSlotMode('manual')}
                                />{' '}
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{ color: '#c9d1d9' }}
                                >
                                  Pick manually
                                </Typography>
                              </label>
                            </Stack>
                            {mlSlotMode === 'suggested' && (
                              <Box sx={{ mb: 2, px: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#8b949e', display: 'block', mb: 0.5 }}
                                >
                                  Nudge time (drag)
                                </Typography>
                                <Slider
                                  size="small"
                                  value={mlSlotOffsetMinutes}
                                  onChange={(_, v) => setMlSlotOffsetMinutes(v)}
                                  min={-48 * 60}
                                  max={72 * 60}
                                  step={15}
                                  valueLabelDisplay="auto"
                                  valueLabelFormat={(v) => formatMlOffsetMinutes(v)}
                                  sx={{ color: '#8957e5' }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ color: '#6e7681', display: 'block' }}
                                >
                                  {formatMlOffsetMinutes(mlSlotOffsetMinutes)}
                                </Typography>
                              </Box>
                            )}
                            {mlSlotMode === 'manual' && (
                              <TextField
                                fullWidth
                                size="small"
                                type="datetime-local"
                                label="Manual time"
                                value={mlSlotManualTime}
                                onChange={(e) =>
                                  setMlSlotManualTime(e.target.value)
                                }
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                  mb: 1.5,
                                  maxWidth: 320,
                                  '& .MuiOutlinedInput-root': {
                                    bgcolor: '#161b22',
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#8b949e',
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    color: '#f0f6fc',
                                  },
                                }}
                              />
                            )}
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={
                                  mlSlotCommitting ? (
                                    <CircularProgress
                                      size={16}
                                      color="inherit"
                                    />
                                  ) : (
                                    <CalendarMonth />
                                  )
                                }
                                disabled={
                                  mlSlotCommitting ||
                                  (mlSlotMode === 'manual' &&
                                    !mlSlotManualTime)
                                }
                                onClick={handleScheduleWithMlCommit}
                                sx={{
                                  bgcolor: '#238636',
                                  '&:hover': { bgcolor: '#2ea043' },
                                }}
                              >
                                Accept &amp; commit
                              </Button>
                              <Button
                                variant="text"
                                size="small"
                                disabled={mlSlotCommitting}
                                onClick={() => {
                                  setMlSlotPreview(null);
                                  setMlSlotOffsetMinutes(0);
                                }}
                                sx={{ color: '#8b949e' }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </Box>
                        )}
                        <Divider
                          sx={{ borderColor: '#30363d', my: 2 }}
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#8b949e', mb: 1 }}
                        >
                          Hashtag strategy (Gemini — brand + niche per platform)
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mb: 2, flexWrap: 'wrap' }}
                          useFlexGap
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AutoAwesome />}
                            disabled={captionBusy}
                            onClick={handleGenerateHashtagStrategy}
                          >
                            Generate / refresh hashtag strategy
                          </Button>
                        </Stack>
                        {activeDraft?.hashtag_strategy ? (
                          <Box>
                            <Tabs
                              value={hashtagPlatformTab}
                              onChange={(_, v) => setHashtagPlatformTab(v)}
                              sx={{
                                minHeight: 36,
                                '& .MuiTab-root': {
                                  color: '#8b949e',
                                  minHeight: 36,
                                  py: 0.5,
                                },
                                '& .Mui-selected': { color: '#58a6ff' },
                              }}
                            >
                              <Tab label="Instagram" />
                              <Tab label="Facebook" />
                              <Tab label="LinkedIn" />
                            </Tabs>
                            <Box sx={{ mt: 1.5 }}>
                              {(() => {
                                const pk = (
                                  ['instagram', 'facebook', 'linkedin'][
                                    hashtagPlatformTab
                                  ] || 'instagram'
                                );
                                const rows =
                                  activeDraft.hashtag_strategy[pk] || [];
                                return localVariants.map((_, idx) => {
                                  const row = rows[idx] || {};
                                  const brand = row.brand_tags || [];
                                  const niche = row.niche_tags || [];
                                  const all = row.hashtags || [];
                                  return (
                                    <Box
                                      key={`hs-${pk}-${idx}`}
                                      sx={{
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '1px solid #30363d',
                                        bgcolor: '#0d1117',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#f0f6fc',
                                          fontWeight: 600,
                                          display: 'block',
                                          mb: 1,
                                        }}
                                      >
                                        {(row.style || `variant ${idx}`)
                                          .replace(/_/g, ' ')}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#8b949e',
                                          display: 'block',
                                          mb: 0.5,
                                        }}
                                      >
                                        Brand
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        useFlexGap
                                        spacing={0.5}
                                        sx={{ mb: 1 }}
                                      >
                                        {brand.length ? (
                                          brand.map((t, ti) => (
                                            <Chip
                                              key={`b-${idx}-${ti}-${t}`}
                                              size="small"
                                              label={`#${t}`}
                                              sx={{
                                                height: 22,
                                                fontSize: 11,
                                                bgcolor: '#21262d',
                                              }}
                                            />
                                          ))
                                        ) : (
                                          <Typography
                                            variant="caption"
                                            sx={{ color: '#484f58' }}
                                          >
                                            —
                                          </Typography>
                                        )}
                                      </Stack>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#8b949e',
                                          display: 'block',
                                          mb: 0.5,
                                        }}
                                      >
                                        Niche (pillar)
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        flexWrap="wrap"
                                        useFlexGap
                                        spacing={0.5}
                                        sx={{ mb: 1 }}
                                      >
                                        {niche.length ? (
                                          niche.map((t, ti) => (
                                            <Chip
                                              key={`n-${idx}-${ti}-${t}`}
                                              size="small"
                                              label={`#${t}`}
                                              sx={{
                                                height: 22,
                                                fontSize: 11,
                                                bgcolor: '#1f3d2f',
                                              }}
                                            />
                                          ))
                                        ) : (
                                          <Typography
                                            variant="caption"
                                            sx={{ color: '#484f58' }}
                                          >
                                            —
                                          </Typography>
                                        )}
                                      </Stack>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: '#8b949e',
                                          display: 'block',
                                          mb: 0.5,
                                        }}
                                      >
                                        Full set (posting order)
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: '#c9d1d9',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {all.length
                                          ? all.map((t) => `#${t}`).join(' ')
                                          : '—'}
                                      </Typography>
                                    </Box>
                                  );
                                });
                              })()}
                            </Box>
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: '#484f58' }}
                          >
                            Run &quot;Generate / refresh hashtag strategy&quot;
                            after captions are ready. New caption runs include
                            this automatically when Gemini is configured.
                          </Typography>
                        )}
                      </>
                    )}
                </>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={Boolean(hardDeleteConfirm)}
        onClose={() => setHardDeleteConfirm(null)}
        PaperProps={{ sx: { bgcolor: '#161b22', border: '1px solid #30363d' } }}
      >
        <DialogTitle sx={{ color: '#f0f6fc' }}>Delete permanently?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#c9d1d9' }}>
            This removes the file and library row. Prefer Archive if you still want
            engagement data for ML. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setHardDeleteConfirm(null)} sx={{ color: '#8b949e' }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              hardDeleteConfirm && archiveBusyId === hardDeleteConfirm.id
            }
            onClick={() =>
              hardDeleteConfirm && handleHardDeleteAsset(hardDeleteConfirm)
            }
          >
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
